import { Component, OnInit, OnDestroy, ChangeDetectorRef, HostListener, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../../../../services/api.service';
import moment from 'moment';
import { firstValueFrom, Subscription, Observable, forkJoin, of } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { CommonFunctions } from '../../../../utils/common-functions';
import { AlertService } from '../../../../services/alert.service';
import { AutoUnsubscribe } from '../../../../decorators/auto-unsubscribe';
import {
  getCellValidationFailure,
  isValidValueCell,
  isWarningCell,
  taxReturnCellFlagCopy,
  notNumberWithDecimalCommaDashRegex,
  selectionsOverlap,
  mergeSelections,
  isValidDateCell,
  CellErrorManager,
  FrontendValidationManager,
  BackendValidationManager,
  VALIDATION_STATUS,
  PageStatusStoreType,
  ERROR_STATUS, WARNING_STATUS,
  LabelExceedsCharLimitCopy,
  LabelExceedsCharLimitHeading,
  LabelExceedsCharLimitDescription
} from './validation';
import { UserService } from '../../../../services/user.service';
import { SharedDataService } from '../../../../services/shared-data.service';
import { EnvironmentService } from '../../../../services/environment.service';
import { StatementOption } from '../../../../models/statement-option';
import { DecimalPipe } from '@angular/common';
import { SelectedCells } from '../../../../models/review-select-cells';
import { ReviewQueueItem, ReviewQueueItemInterface } from '../../../../models/review-queue-item';
import { ReviewQueueService } from '../../../../services/review-queue.service';
import {
  DOC_PROCESSING_STATUS,
  SUPPORTED_CURRENCIES,
  DOCUMENT_FILE_TYPE,
  USER_GUIDES,
  OPTION_LABEL_CELL_STYLE,
  WARNING_CELL_STYLE,
  PROBLEM_CELL_STYLE,
  SCENARIO_TYPE_HISTORICAL,
  SCENARIO_TYPE_PROJECTION,
  STATEMENT_DATE,
  SCENARIO_TYPE,
  MANUAL_REVIEW_HEADER_ORDER,
  PREPARATION_TYPES_FOR_PROJECTIONS,
  PREPARATION_TYPES_FOR_HISTORICAL,
  PREPARATION_TYPE_TAX_RETURN_KEY,
  REPORTING_INTERVAL_ANNUAlLY_KEY,
  SUPPORTED_VIEW_SOURCE_FILE_TYPES,
  UNKNOWN_ERROR_TOAST_BODY,
  UNKNOWN_ERROR_TOAST_TITLE,
} from '@utils/constants';
import { CurrencyFormattingService } from '../../../../services/currency-formatting.service';
import { DocumentFileService } from '../../../../services/document-file.service';
import { TranslationService } from '../../../../services/translation.service';
import { InstructionsStep } from '../../../../models/instructions-step';
import { TrackingService } from '../../../../services/tracking.service';
import { PreviousRouteService } from '../../../../services/previous-route.service';
import { LoggingService, Logger } from '../../../../services/logging.service';
import { TAX_RETURN, MANUAL_REVIEW_HEADERS, MANUAL_REVIEW_HEADERS_INCLUDING_LEGACY, canonicalMetaLabel, MONTH_FINAL_DAYS, LEAP_YEAR_MONTH_FINAL_DAYS, BEDROCK_TABLE_METADATA_FLAG } from '../../../../utils/constants'
import { UserGuideService } from '@services/user-guide.service';
import { UserGuide } from '@models/user-guide';
import Handsontable from 'handsontable'
import { HotTableRegisterer } from '@handsontable/angular';
import {
  pushReviewEditorAction,
  REVIEW_ACTION_TYPES,
  ReviewEditorAction,
  ReviewEditorSnapshot,
  SelectionRef,
} from './undo-actions';
import {
  patchSnapshotForColumnInsert,
  patchSnapshotForInsertFrom,
  patchSnapshotForRowInsert,
  shiftSelectionForColumnInsert,
  shiftSelectionForRowInsert,
  shiftSelectionForRowInsertBy,
  walkActivePageActions,
} from './undo-patching';
import { ForceReloadService } from '@services/force-reload.service';
import {LaunchDarklyService} from "@services/launchdarkly.service";
import { StatusModalComponent } from '../human-validation/shared/status-modal/status-modal.component';
import { NgxPopupService, NgxPopupComponent } from '../../../shared/ngx-popups/ngx-popups/ngx-popups';
import { FINAL_REVIEW_STEP } from '@utils/constants';
import { StepLabelService } from '@services/step-label.service';
import { TranslationViewStateService } from '@services/translation-view-state.service';
const ValidationTooltipDelayTime = 500;
const integerRegex = /\d+/;
const INITIAL_INVALID_VALUE = 'INITIAL_INVALID_FLAG_FROM_SERVER';
const REVIEW_TABLE_ROW_RENDER_BUFFER = 100;

interface ReviewEditorCell {
  text?: string;
  rawText?: string;
  raw_text?: string;
  translatedRawText?: string;
  translated_raw_text?: string;
  value?: number;
  sourceBox?: { rawText?: string; [key: string]: any } | null;
  pageNumber?: number;
  footnotes?: any[];
}

@Component({
    selector: 'app-review-editor',
    templateUrl: './review-editor.component.html',
    styleUrls: ['./review-editor.component.scss'],
    providers: [DecimalPipe],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class ReviewEditorComponent implements OnInit, OnDestroy {
  clipboardCache: any = [];

  /** Flag-dependent data-review guide added on init, so only the one actually added gets removed on destroy. */
  private _activeDataReviewGuide: UserGuide = null;

  cellErrors: CellErrorManager = new FrontendValidationManager();
  cellErrorsFromBackendValidation: BackendValidationManager = new BackendValidationManager();
  pageStatuses: PageStatusStoreType = {};
  ERROR_STATUS = ERROR_STATUS;
  WARNING_STATUS = WARNING_STATUS;
  // AI disclaimer banner gating flag for the Format Data step (AUTO-3408).
  readonly bedrockTableMetadataFlag = BEDROCK_TABLE_METADATA_FLAG;
  duplicateSelectedStatementType: 'INCOME_STATEMENT' | 'BALANCE_SHEET' | null;

  handleStatementTypeSelectorChange() {
    // clear the 'duplicate values selected' error highlighting across all pages on any change
    this.duplicateSelectedStatementType = null;
    this.model.document.pages.forEach((_, index) => this.assignPageValidationStatus(index));
    this.addInsertIntoTableOptions();
  }

  assignPageValidationStatus(page) {
    // AUTO-2822: prune backend-store entries whose coord no longer exists on the
    // shrunken grid (would otherwise force the tab red via Override A). Frontend
    // reconciliation is handled by validatePage() calls added to the mutation paths
    // that need them (removeRows/removeColumns/mergeColumns — matches mergeRows).
    this._pruneBackendStoreBeyondBounds(page);

    this.pageStatuses[page] = this.cellErrors.getPageStatus(page);
    if (this.cellErrorsFromBackendValidation.getPageStatus(page) === ERROR_STATUS || !this._pageHasValidStatementTypeSelected(page)) {
      this.pageStatuses[page] = ERROR_STATUS;
    }
  }

  /**
   * Drop backend-error entries whose (row, col) is past the current grid — those
   * entries would otherwise force the tab red via Override A even though the flagged
   * cell no longer exists (e.g. the user removed the row containing a save-flagged cell).
   */
  private _pruneBackendStoreBeyondBounds(page: number): void {
    const store = this.cellErrorsFromBackendValidation.errorStore?.[page];
    if (!store) { return; }
    const pageObj = this.model?.document?.pages?.[page];
    if (!pageObj) { return; }
    const totalRows = (pageObj.metadata?.length ?? 0) + (pageObj.cells?.length ?? 0);
    const totalCols = pageObj.cells?.[0]?.length ?? pageObj.metadata?.[0]?.length ?? 0;
    for (const key of Object.keys(store)) {
      const [r, c] = key.split('_').map(Number);
      if (r >= totalRows || c >= totalCols) {
        delete store[key];
      }
    }
  }

  /**
   * Shift a page-indexed map down by one at every index above the deleted page, so
   * entries that used to live at index N+1 now live at index N. Used by deletePage.
   */
  private _compactMapByPageIndex(map: Record<number, unknown>, deletedPage: number): void {
    if (!map) { return; }
    delete map[deletedPage];
    const higherKeys = Object.keys(map)
      .map(Number)
      .filter(k => k > deletedPage)
      .sort((a, b) => a - b);
    for (const k of higherKeys) {
      map[k - 1] = map[k];
      delete map[k];
    }
  }

  _pageHasValidStatementTypeSelected(page){
    if (this.model.document.pages[page].statementType == this.duplicateSelectedStatementType){
      return false
    }
    return ['INCOME_STATEMENT','BALANCE_SHEET','CASH_FLOW_STATEMENT'].includes(this.model.document.pages[page].statementType)
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    // When the viewport changes (e.g. DevTools open/close), refresh Handsontable dimensions
    setTimeout(() => {
      const hotInstance = this.hotRegisterer?.getInstance(this.hotId);
      if (hotInstance) {
        hotInstance.refreshDimensions();
        hotInstance.render();
      }
    }, 0);
  }

  private static getPageMetadataScenarioTypes(page) {
    for (let i = 0; i < page.metadata.length; ++i) {
      if (page.metadata[i][0] === SCENARIO_TYPE) {
        return page.metadata[i];
      }
    }
    return null;
  }

  private static setPrepTypeOptionsUsingScenarioType(page, reviewQueueItem: ReviewQueueItemInterface, statementOptions: Array<Array<StatementOption>>) {
    statementOptions.forEach((statementOption, idx) => {
      if (idx > 0){ // if 0, then it's a line item row without numerical data
        if (page.metadata[MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX][idx] == SCENARIO_TYPE_HISTORICAL) { // index 2 is scenario_type
          statementOption[MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX].values = Object.keys(PREPARATION_TYPES_FOR_HISTORICAL)
        } else if (page.metadata[MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX][idx] == SCENARIO_TYPE_PROJECTION) {
          statementOption[MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX].values = Object.keys(PREPARATION_TYPES_FOR_PROJECTIONS)
        }
      }
    });
  }

  private static setMetadataScenarioTypeUsingDate(page, reviewQueueItem: ReviewQueueItemInterface) {
    if (reviewQueueItem.reviewed) {
      // We do not want to set the "Scenario Type" if the user has already been through the "Data Review" screen
      // because the user will have had the chance to "Scenario Type" so we do not want to change the user's choice.
      return;
    }

    let statementDates = page.metadata.filter(metadata => metadata[0] === STATEMENT_DATE);

    const pageScenarioTypes = ReviewEditorComponent.getPageMetadataScenarioTypes(page);

    if (statementDates.length === 0 || pageScenarioTypes === null) {
      return;
    } else {
      statementDates = statementDates[0];
    }

    for (let i = 1; i < statementDates.length; ++i) {
      if (statementDates[i] !== '') {
        if (new Date(statementDates[i]) < new Date()) {
          pageScenarioTypes[i] = SCENARIO_TYPE_HISTORICAL;
        } else {
          pageScenarioTypes[i] = SCENARIO_TYPE_PROJECTION;
        }
      }
    }
  }

  /**
   * Determines if a cell is a header cell, i.e. the cell is in the first column and in a header row.
   */
  private static cellIsAHeader(
    rowNumber: number,
    columnNumber: number,
    statementOptions: Array<Array<Array<StatementOption>>>,
    activePageNumber: number
  ): boolean {
    const headerColumnNumber = 0;
    // A new page that is created does not populate with statement options so we cannot rely on the object
    if (statementOptions[activePageNumber].length === 0) {
      return false
    }

    return rowNumber < statementOptions[activePageNumber][columnNumber]?.length && columnNumber === headerColumnNumber;
  }

  /**
   * Determines if the cell being operated on is the a preparation type cell.
   * @param page - The page that contains the rows and columns of data of interest.
   * @param columnNumber - The column number used to determine if the column is a preparation type.
   * @private
   */
  private static cellIsPreparationTypeData(page, columnNumber: number): boolean {
    return page.metadata[MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX][columnNumber] === SCENARIO_TYPE_PROJECTION;
  }

  /**
   * Determines if the cell at the position [rowNumber, columnNumber] contains a value of a header column and is in a
   * data column rather than the categorization label column.
   * @param rowNumber - The row number to be used to determine if the row is a header row.
   * @param columnNumber - The column number used to determine if the column a data column or
   * a categorization label column.
   * @private
   */
  private static cellIsInDataColumnAndIsHeaderRow(rowNumber: number, columnNumber: number): boolean {
    const categorizationLabelColumnNumber = 0;
    return rowNumber <= Object.keys(MANUAL_REVIEW_HEADER_ORDER).length - 1 &&
      columnNumber > categorizationLabelColumnNumber;
  }

  /**
   * Determines if the cell at the position [rowNumber, columnNumber] contains a value of a header column and is in the
   * scenario type header row.
   * @param rowNumber - The row number to be used to determine if this row is a header column.
   * @param columnNumber - The column number to be used to determine if this column is a data column or
   * categorization label column.
   * @private
   */
  private static cellIsInDataColumnAndIsScenarioTypeHeaderRow(rowNumber: number, columnNumber: number): boolean {
    const categorizationLabelColumnNumber = 0;
    return columnNumber > categorizationLabelColumnNumber &&
      rowNumber === MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX;
  }

  subsArr$: Subscription[] = [];
  private statusModalPopup: NgxPopupComponent = null;
  private isCompletingReview = false;
  currentScreen = 'menu';
  reviewQueueItemId = null;
  reviewQueueItem: ReviewQueueItem = null;
  hotId = 'manualReview';
  supportedCurrencies = SUPPORTED_CURRENCIES;
  model: ReviewQueueItemInterface = {
    'id': '',
    'companyId': -1,
    'taskToken': '',
    'lockKey': '',
    'status': '',
    'documentFileId': -1,
    'reviewed': false,
    'suffix': '',
    'step': '',
    'bankId': '',
    'currency': '',
    'multiplyByFactor': null,
    'document': {
      'execution_id': '',
      'pages': [],
      'fileKey': '',
      'type': DOCUMENT_FILE_TYPE.UNKNOWN
    },
    'originalFileUrl': '',
    'jobPayload': {
      'documentFile': {
        'id': '',
        'name': '',
        'reportedDate': '',
        'statementDate': '',
        'requestedPeriods': '',
        'additionalInformation': ''
      },
      'company': {
        'id': null,
        'name': null
      },
      'context': {
        'bankId': null
      },
      'original_document_name': ''
    }
  };

  error = null;
  modelLoadError = null;
  isError = false;
  canceled = false;
  activePage = 0;
  // Per-tab undo/redo stacks. The plugin's live doneActions/undoneActions only ever
  // hold the active tab's actions; on tab switch they're saved here and the new
  // tab's stacks are restored. Eliminates cross-tab undo applying to the wrong page.
  private pageHistories = new Map<number, { doneActions: any[]; undoneActions: any[] }>();
  pageData: string[][] = null;
  reportingInterval;
  statementDate;
  isLoading = true;
  splitscreenOpen = false;
  roundingSelection: string | null = null;
  currency: string;
  isRoundingDropdownInvalid: boolean = false;
  isRoundingDropdownDisabled: boolean = false;
  modelCopy: any;
  selectedCells: SelectedCells[] = [];
  cellValidationMessage = '';
  cellValidationHeading = '';
  cellValidationStyle = {};
  statementOptions: Array<Array<Array<StatementOption>>>;
  handsOnTableInstance: Handsontable = null;
  translationDict: any = {};
  decimalCounts: Array<number> = []
  numDecimalsMode = 0;
  viewInNativeLanguage = true; // Default to showing original/native labels

  @ViewChild('mainColumn') mainColumn?: ElementRef<HTMLDivElement>;

  // Data passed to hands on table for column formatting and headers
  columns: Array<Object> = [];

  // data passed to split screen component for viewing source document
  viewSource = false;
  viewSourcePage = null;
  viewSourceBox = null;

  logger: Logger;
  handsOnTableLicense = '';

  // Split screen resize state
  leftPaneWidthPercent = 50;
  rightPaneWidthPercent = 50;
  private isResizingSplit = false;
  private lastClientX: number | null = null;
  // Extended Sidebar instructions
  // eslint-disable-next-line max-len
  stepHeader = 'In this step, you will review your digitized statements to ensure that the data is correct and well-formatted. Hover your mouse over any red or yellow cells to see why there may be issues.';
  steps: InstructionsStep[] = [
    {
      title: 'Open original',
      body: 'Click \'Open original document\' next to the file name at the top of the page.',
      gifName: 'FR_1_Open_Original_Doc_updated_5_3_2021.gif'
    }, {
      title: 'Add header information',
      body: 'Reporting Interal (required)<br><br>Preparation Type (required)<br><br>Analyst (optional)<br><br>Statement Date (required)',
      gifName: 'FR_2_add_header_information_updated_5_3_2021.gif'
    }, {
      title: 'Clean up data structure',
      body: 'Keep line item labels in the first column<br />Keep number values in the remaining columns<br />Remove columns covering unwanted periods',
      gifName: '',
    }, {
      title: 'Validate data',
      body: 'Use the “Show Source” feature to verify number values with the original document ',
      gifName: 'FR_4_validate_data_updated_5_3_2021.gif',
    }, {
      title: 'Finish',
      body: 'Click \'Next\' to move onto Categorization',
      gifName: '',
    }
  ];

  tipsBody = 'Cells that contain invalid data will be highlighted in red, you will not be able to move forward until you resolve the issue. \nOther cells that may need your attention will be highlighted in yellow, you do not need to change these cells to move forward.\nHover your mouse over the highlighted cells to see what the issues may be and how to resolve them.';

  contextMenu = {
    items: {
      'copy': {
        name: 'Copy',
        disabled: () => !this.viewInNativeLanguage && this._selectionIncludesTranslatedLabelColumn()
      },
      'paste': {
        name: this.pasteHelperText(),
        disabled: () => (!this.viewInNativeLanguage && this._selectionIncludesTranslatedLabelColumn()) || !this.clipboardCache || this.clipboardCache.length === 0,
        callback: (key, selection, clickEvent) => {
          this.handsOnTableInstance.listen();
          let plugin = this.handsOnTableInstance.getPlugin('copyPaste');
          plugin.paste(this.clipboardCache);
        }
      },
      // Mirrors Handsontable's default stack-based disabling (isUndoAvailable/isRedoAvailable),
      // additionally disabled while the translate toggle is on.
      'undo': {
        disabled: () => !this.viewInNativeLanguage || !this.isUndoActionAvailable()
      },
      'redo': {
        disabled: () => !this.viewInNativeLanguage || !this.isRedoActionAvailable()
      },
      'sanitize_cells': {
        'name': 'Remove Punctuation',
        callback: (key, selection, clickEvent) => {
          this.cleanSelectedCells();
        },
        disabled: () => !this.viewInNativeLanguage && this._selectionIncludesTranslatedLabelColumn()
      },
      'insert_from_page': {
        name: 'Insert From',
        disabled: true,
        submenu: {
          items: []
        }
      },
      'separator1': '---------',
      'insert_rows': {
        name: 'Insert Rows',
        disabled: false,
        submenu: {
          items: [
            {
              key: 'insert_rows:insert_row_above',
              'name': 'Insert row above',
              callback: (key, selection, clickEvent) => {
                this.insertRow(selection, true);
              },
              disabled: () => {
                if (!this.handsOnTableInstance) {
                  return false;
                }
                const selectedRange = this.handsOnTableInstance.getSelected();
                if (selectedRange.length > 0) {
                  const rowNum = selectedRange[0][0];
                  if (rowNum > this.pageData.length || rowNum < this.model.document.pages[this.activePage].metadata.length) {
                    return true;
                  }
                }
                return false;
              }
            }, {
              key: 'insert_rows:insert_row_below',
              'name': 'Insert row below',
              callback: (key, selection, clickEvent) => {
                this.insertRow(selection, false);
              },
              disabled: () => {
                if (!this.handsOnTableInstance) {
                  return false;
                }
                const selectedRange = this.handsOnTableInstance.getSelected();
                if (selectedRange.length > 0) {
                  const rowNum = selectedRange[0][0];
                  if (rowNum > this.pageData.length || rowNum < this.model.document.pages[this.activePage].metadata.length) {
                    return true;
                  }
                }
                return false;
              }
            }, {
              key: 'insert_rows:insert_5_rows_above',
              'name': 'Insert 5 rows above',
              callback: (key, selection, clickEvent) => {
                for (let i = 0; i < 5; i++) {
                  this.insertRow(selection, true);
                }
              },
              disabled: () => {
                if (!this.handsOnTableInstance) {
                  return false;
                }
                const selectedRange = this.handsOnTableInstance.getSelected();
                if (selectedRange.length > 0) {
                  const rowNum = selectedRange[0][0];
                  if (rowNum > this.pageData.length || rowNum < this.model.document.pages[this.activePage].metadata.length) {
                    return true;
                  }
                }
                return false;
              }
            }, {
              key: 'insert_rows:insert_5_rows_below',
              'name': 'Insert 5 rows below',
              callback: (key, selection, clickEvent) => {
                for (let i = 0; i < 5; i++) {
                  this.insertRow(selection, false);
                }
              },
              disabled: () => {
                if (!this.handsOnTableInstance) {
                  return false;
                }
                const selectedRange = this.handsOnTableInstance.getSelected();
                if (selectedRange.length > 0) {
                  const rowNum = selectedRange[0][0];
                  if (rowNum > this.pageData.length || rowNum < this.model.document.pages[this.activePage].metadata.length) {
                    return true;
                  }
                }
                return false;
              }
            },
          ]
        }
      },
      'remove_rows': {
        'name': 'Remove rows',
        callback: (key, selection, clickEvent) => {
          this.removeRows(selection);
        },
        disabled: () => {
          if (!this.handsOnTableInstance) {
            return false;
          }
          const selectedRange = this.handsOnTableInstance.getSelected();
          if (selectedRange.length > 0) {
            const rowNum = selectedRange[0][0];
            if (rowNum > this.pageData.length || rowNum < this.model.document.pages[this.activePage].metadata.length) {
              return true;
            }
          }
          return false;
        }
      },
      'merge_rows': {
        'name': 'Merge Rows',
        callback: (key, selection, clickEvent) => {
          this.mergeRows(selection);
        },
        disabled: () => this.mergeRowsDisabled(),
      },
      'separator2': '---------',
      'insert_columns': {
        name: 'Insert Columns',
        disabled: false,
        submenu: {
          items: [
            {
              key: 'insert_columns:insert_col_left',
              'name': 'Insert column left ',
              callback: (key, selection, clickEvent) => {
                this.insertColumn(selection, true);
              },
              disabled: () => {
                if (!this.handsOnTableInstance) {
                  return false;
                }

                const firstColumnIndex = 0;
                const colIndexWhenSelectingARow = -1;
                const selectedRange = this.handsOnTableInstance.getSelected();

                if (selectedRange.length > 0) {
                  const colNum = selectedRange[0][1];
                  if (colNum === firstColumnIndex || colNum === colIndexWhenSelectingARow) {
                    return true;
                  }
                }
                return false;
              }
            }, {
              key: 'insert_columns:insert_col_right',
              'name': 'Insert column right ',
              callback: (key, selection, clickEvent) => {
                this.insertColumn(selection, false);
              }
            }, {
              key: 'insert_columns:insert_5_col_left',
              'name': 'Insert 5 columns left ',
              callback: (key, selection, clickEvent) => {
                for (let i = 0; i < 5; i++) {
                  this.insertColumn(selection, true);
                }
              },
              disabled: () => {
                if (!this.handsOnTableInstance) {
                  return false;
                }

                const firstColumnIndex = 0;
                const colIndexWhenSelectingARow = -1;
                const selectedRange = this.handsOnTableInstance.getSelected();

                if (selectedRange.length > 0) {
                  const colNum = selectedRange[0][1];
                  if (colNum === firstColumnIndex || colNum === colIndexWhenSelectingARow) {
                    return true;
                  }
                }
                return false;
              }
            }, {
              key: 'insert_columns:insert_5_col_right',
              'name': 'Insert 5 columns right ',
              callback: (key, selection, clickEvent) => {
                for (let i = 0; i < 5; i++) {
                  this.insertColumn(selection, false);
                }
              }
            },
          ]
        }
      },
      'remove_cols': {
        'name': 'Remove columns',
        callback: (key, selection, clickEvent) => {
          this.removeColumns(selection);
        },
        disabled: () => {
          if (!this.handsOnTableInstance) {
            return false;
          }
          const selectedRange = this.handsOnTableInstance.getSelected();
          if (selectedRange.length > 0) {
            const colNum = selectedRange[0][1];
            if (colNum === 0 || colNum === -1) {
              return true;
            }
          }
          return false;
        }
      },
      'merge_columns': {
        'name': 'Merge Cells Left',
        callback: (key, selection, clickEvent) => {
          this.mergeColumns(selection);
        },
        disabled: () => this.mergeColumnsDisabled(),
      },
      'set_reporting_interval_all': {
        'name': 'Set reporting interval for all columns',
        callback: (key, selection, clickEvent) => {
          const rowNum = MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX;
          const interval = this.retrieveValueFromStatementOptionsCell(rowNum, selection);
          this.autoSetStatementOptions(rowNum, interval);
        },
        hidden: () => {
          return this.shouldShowInContextMenu(MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX);
        }
      },
      'set_prep_type_all': {
        'name': 'Set prep type for all columns',
        callback: (key, selection, clickEvent) => {
          const rowNum = MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX;
          const prepType =  this.retrieveValueFromStatementOptionsCell(rowNum, selection);
          this.autoSetStatementOptions(rowNum, prepType);
        },
        hidden: () => {
          return this.shouldShowInContextMenu(MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX);
        }
      },
      'separator4': '---------',
    }
  };

  // Need to do this to have access to the component in the renderer.
  renderCell = this._renderCell.bind(this);

  private hotRegisterer = new HotTableRegisterer();

  constructor(
    private _apiService: ApiService,
    private _alertService: AlertService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _sharedDataService: SharedDataService,
    private _changeDetector: ChangeDetectorRef,
    private _environmentService: EnvironmentService,
    private _userService: UserService,
    private _reviewQueueService: ReviewQueueService,
    private _currencyFormattingService: CurrencyFormattingService,
    private _documentFileService: DocumentFileService,
    private _trackingService: TrackingService,
    private _loggingService: LoggingService,
    private _previousRouteService: PreviousRouteService,
    public userGuideService: UserGuideService,
    public forceReloadService: ForceReloadService,
    private _launchDarklyService: LaunchDarklyService,
    private _popupService: NgxPopupService,

    private _translationService: TranslationService,
    private _stepLabelService: StepLabelService,
    private _translationViewState: TranslationViewStateService,
    ) {
    this.logger = this._loggingService.rootLogger.newLogger('ReviewEditor');
  }


  resizeSplitMouseDown(event: MouseEvent): void {
    if (!this.splitscreenOpen) {
      return;
    }
    event.preventDefault();
    this.isResizingSplit = true;
    this.lastClientX = event.clientX;
    document.body.style.cursor = 'col-resize';
  }

  @HostListener('window:mousemove', ['$event'])
  onWindowMouseMove(event: MouseEvent): void {
    if (!this.isResizingSplit || !this.splitscreenOpen) {
      return;
    }

    const container = document.querySelector('.non-header-content-container') as HTMLElement | null;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    if (rect.width <= 0) {
      return;
    }

    const relativeX = event.clientX - rect.left;
    let newLeft = (relativeX / rect.width) * 100; 
    const minPercent = 19;
    const maxPercent = 53;

    if (newLeft < minPercent) {
      newLeft = minPercent;
    }
    if (newLeft > maxPercent) {
      newLeft = maxPercent;
    }

    this.leftPaneWidthPercent = newLeft;
    this.rightPaneWidthPercent = 100 - newLeft;
    if(this.rightPaneWidthPercent < 47){
      this.rightPaneWidthPercent=47;
    }
  }

  @HostListener('window:mouseup')
  onWindowMouseUp(): void {
    if (this.isResizingSplit) {
      this.isResizingSplit = false;
      this.lastClientX = null;
      document.body.style.cursor = 'default';
    }
  }

  ngOnInit() {
    this.handsOnTableLicense = this._environmentService.getHandsOnTableLicenseKey();
    this.setTopAndSideNavDisplay(false);
    this._activeDataReviewGuide = this._stepLabelService.isEarlyClassificationEnabled
      ? USER_GUIDES.FORMATTING_DATA_EARLY_CLASS
      : USER_GUIDES.FORMATTING_DATA;
    this.userGuideService.add(this._activeDataReviewGuide);
    this.userGuideService.add(USER_GUIDES.REMOVING_DATA);
    this.userGuideService.add(USER_GUIDES.SUPPORTED_TAX_RETURNS);

    this._sharedDataService.setHILExitUrl(this._previousRouteService.getPreviousUrl());

    this.subsArr$.push(this._route.parent.params.subscribe((params => {
      this.reviewQueueItemId = params['uuid'];
      if (!this._reviewQueueService.isValidUUID(this.reviewQueueItemId)) {
        this._router.navigate(['/404']);
        return;
      }

      this.retrieveReviewQueueItem();
    })));
    
    this._showDocumentFlyoutIfEnabled();
  }
  
  _showDocumentFlyoutIfEnabled() {
    const defaultDocViewSetting = this._launchDarklyService.flags['default-show-document-view-in-data-review-and-categorization'];
    if (defaultDocViewSetting) {
      this.openSplitscreen();
    }
  }

  afterTableInit = () => {
    const instance = this.hotRegisterer.getInstance(this.hotId);

    if (instance) {
      this.handsOnTableInstance = instance;
      this.assertUndoRedoPluginShape();
      this.setOptions(instance);
      this.autoSetStatementOptions(0);
      this.autoSetStatementOptions(1);
      this.autoSetStatementOptions(2);
      this.autoSetStatementOptions(3);
      this.autoSetStatementOptions(4);

      this.handsOnTableInstance.addHook('afterViewRender', () => {
        this.assignPageValidationStatus(this.activePage);
      });

      // @ts-ignore
      this.handsOnTableInstance.addHook('afterDocumentKeyDown', (e) => {
        if (e.key === 'm') {
          this.cleanSelectedCells();
        }
      });

      this.handsOnTableInstance.addHook('beforeKeyDown', (e) => {
        if (!this.viewInNativeLanguage) {
          if ((e.ctrlKey || e.metaKey) &&
              (e.key === 'c' || e.key === 'v' || e.key === 'x') &&
              this._selectionIncludesTranslatedLabelColumn()) {
            e.stopImmediatePropagation();
            e.preventDefault();
          }

          // Undo/redo is disabled while viewing translated labels (see contextMenu items).
          // Returning false is the only signal Handsontable's ShortcutManager honors here —
          // native stopImmediatePropagation() does not set the internal flag it checks.
          if ((e.ctrlKey || e.metaKey) &&
              (e.key === 'z' || e.key === 'y' || e.key === 'Z' || e.key === 'Y')) {
            e.preventDefault();
            return false;
          }

          const selected = this.handsOnTableInstance.getSelected();
          if (selected && selected.length > 0) {
            const col = selected[0][1];
            const row = selected[0][0];
            if (col === 0 && this._cellHasTranslation(row, col) &&
                e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
              e.stopImmediatePropagation();
              e.preventDefault();
            }
          }
        }
      });

      // @ts-ignore
      this.handsOnTableInstance.addHook('beforeChange', (changes, source) => {
        if (!this.viewInNativeLanguage && changes) {
          for (let i = changes.length - 1; i >= 0; i--) {
            if (changes[i] && changes[i][1] === 0 &&
                this._cellHasTranslation(changes[i][0], 0)) {
              changes[i] = null;
            }
          }
        }
      });

      // @ts-ignore
      this.handsOnTableInstance.addHook('afterChange', (cellChange: Array<[number, number, string, string]>) => {
        this._onAfterChange(cellChange);
      });

      this.handsOnTableInstance.addHook('afterCopy', (data, coords) => {
        if (!this.viewInNativeLanguage && this._selectionIncludesTranslatedLabelColumn()) return;
        this.clipboardCache = data;
      });

      this.handsOnTableInstance.addHook('afterCut', (data, coords) => {
        if (!this.viewInNativeLanguage && this._selectionIncludesTranslatedLabelColumn()) return;
        this.clipboardCache = data;
      });

      this.handsOnTableInstance.addHook('afterSelectionEnd', (row: number, col: number) => {
        if (this.splitscreenOpen && row >= 0 && col >= 0) {
          this.showSource([{ start: { row, col }, end: { row, col } }], false);
        }
      });
      this.validateAllPages();
      for (let pageIndex = 0; pageIndex < this.model.document.pages.length; pageIndex++) {
        this._validateMetadataForPage(pageIndex);
      }
    }
  }

  validateAllPages() {
    /**
     * Loop through each page and validate each cell to determine the initial state of each tab on page load. This logic
     * mirrors the validation performed in the _renderCell function, which is only executed on the active/visible tab.
     */
    this.cellErrors.reset();
    for (let pageIndex = 0; pageIndex < this.model.document.pages.length; pageIndex++) {
      this._runValidationForPage(pageIndex);
    }
  }

  validatePage(pageIndex: number): void {
    this.cellErrors.clearPage(pageIndex);
    this._runValidationForPage(pageIndex);
  }

  private _runValidationForPage(pageIndex: number): void {
    const page = this.model.document.pages[pageIndex];
    if (!page || !page.cells) {
      return;
    }
    // AUTO-2822: resolve each cell to the SAME string the grid/renderer will see,
    // by mirroring _setPageTabularData exactly (value → translatedRawText → text).
    // Previously we fell back straight to col.text when col.value was null, which
    // caused the validator to see a raw multi-dot string like "174.017.706.46" while
    // the grid rendered the cleaned "17401770646" — the two then classified the same
    // cell differently (InvalidValue → ERROR vs PossibleNumDecimals → WARNING),
    // flipping the tab red on save.
    const resolveCellText = (col: any, colIndex: number): string => {
      if (col == null) { return ''; }
      if (typeof col === 'string') { return col; }
      const translatedText = col.translatedRawText || col.translated_raw_text || '';
      if (colIndex === 0) {
        if (this.viewInNativeLanguage) {
          return col.text ?? '';
        }
        return translatedText || this.translationDict[col.text] || col.text || '';
      }
      if (col.value != null) { return String(col.value); }
      return translatedText || col.text || '';
    };

    const pageDecimalCounts: number[] = [];
    page.cells.forEach(row => {
      row.forEach((col, colIndex) => {
        const resolved = resolveCellText(col, colIndex);
        if (colIndex !== 0 && resolved !== '') {
          pageDecimalCounts.push(CommonFunctions.numDecimals(resolved));
        }
      });
    });
    const pageDecimalMode = pageDecimalCounts.length
      ? CommonFunctions.mostFrequentElement(pageDecimalCounts)
      : -1;

    for (let rowIndex = 0; rowIndex < page.cells.length; rowIndex++) {
      const row = page.cells[rowIndex];
      for (let cellIndex = 0; cellIndex < row.length; cellIndex++) {
        const cell = row[cellIndex];
        const cellText = resolveCellText(cell, cellIndex);
        let offsetRowIndex = rowIndex;
        if (this.statementOptions[pageIndex][cellIndex]){
            offsetRowIndex = rowIndex + this.statementOptions[pageIndex][cellIndex].length;
        }
        const validationFailure = getCellValidationFailure(pageIndex, offsetRowIndex, cellIndex, cellText,
          this.statementOptions, this._currencyFormattingService.getDecimalSeparator(this.model.currency),
          this.model.document.type, pageDecimalMode, this.handsOnTableInstance);
        if (!!validationFailure) {
          this.setCellStatusForValidationFailure(cellText, pageIndex, offsetRowIndex, cellIndex, validationFailure)
        }
      }
    }
    this.assignPageValidationStatus(pageIndex);
  }

  private _validateMetadataForPage(pageIndex: number): void {
    const page = this.model?.document?.pages?.[pageIndex];
    if (!page?.metadata?.length) { return; }
    if (!this.statementOptions?.[pageIndex]?.length) {
      this.logger.info('Skipping metadata validation: statementOptions missing for page', { pageIndex });
      return;
    }

    const metadataColCount = page.metadata[0]?.length ?? 0;
    const cellsColCount = page.cells?.[0]?.length ?? metadataColCount;
    const colBound = Math.min(metadataColCount, cellsColCount);
    if (colBound < 2) { return; }

    const lastMetadataRow = Math.min(page.metadata.length - 1, MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX);
    const gridForActivePage = pageIndex === this.activePage ? this.handsOnTableInstance : undefined;
    const decimalSeparator = this._currencyFormattingService.getDecimalSeparator(this.model.currency);

    for (let rowIndex = 0; rowIndex <= lastMetadataRow; rowIndex++) {
      for (let col = 1; col < colBound; col++) {
        const rawText = page.metadata[rowIndex]?.[col];
        const text = rawText == null ? '' : String(rawText);
        const validationFailure = getCellValidationFailure(pageIndex, rowIndex, col, text,
          this.statementOptions, decimalSeparator, this.model.document.type, -1, gridForActivePage);
        if (!!validationFailure) {
          this.setCellStatusForValidationFailure(text, pageIndex, rowIndex, col, validationFailure);
        } else {
          this.cellErrors.clearCellStatus(pageIndex, rowIndex, col);
        }
      }
    }
    this.assignPageValidationStatus(pageIndex);
  }

  setCellStatusForValidationFailure(cell, page, row, col, validationFailure){
    let status = ERROR_STATUS
    if (isWarningCell(cell, row, col, validationFailure)){
      status = WARNING_STATUS
    }
    this.cellErrors.setCellStatus(page, row, col, status)
  }

  cleanSelectedCells(): void {
    // strip cells of non numeric chars - used to convert to whole dollars
    const selected = this.handsOnTableInstance.getSelected();
    if (selected) {
      for (let index = 0; index < selected.length; index += 1) {
        const item = selected[index];
        const startRow = Math.min(item[0], item[2]);
        const endRow = Math.max(item[0], item[2]);
        const startCol = Math.min(item[1], item[3]);
        const endCol = Math.max(item[1], item[3]);

        for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
          for (let columnIndex = startCol; columnIndex <= endCol; columnIndex += 1) {
            if (columnIndex === 0 && !this.viewInNativeLanguage) continue;
            const origRawValue = this.handsOnTableInstance.getDataAtCell(rowIndex, columnIndex);
            let origValue = '';
            if (origRawValue) {
              origValue = origRawValue.toString();
            }
            const cleanValue = this._reviewQueueService.cleanCellPunctuation(origValue);
            if (origValue !== cleanValue) {
              this.handsOnTableInstance.setDataAtCell(rowIndex, columnIndex, cleanValue);
            }
          }
        }
      }
      this.handsOnTableInstance.render();
    }
  }

  shouldShowInContextMenu(rowNum): boolean {
    if (!this.handsOnTableInstance) {
      return false;
    }
    const selectedRange = this.handsOnTableInstance.getSelected();

    if (selectedRange.length > 0) {
      const start_row = selectedRange[0][0];
      const end_row = selectedRange[0][2];
      if (start_row !== rowNum || end_row !== rowNum || selectedRange.length > 1) {
        return true;
      }
    }
    return false;
  }

  retrieveValueFromStatementOptionsCell(rowNum, selection): string {
    let start_col = selection[0]['start']['col'];
    if (start_col === 0) {
      start_col = 1;
    }
    return this.handsOnTableInstance.getDataAtCell(rowNum, start_col);
  }

  private _validateBeforeSave(): boolean {
    if (this.isLoading || this.isCompletingReview) {
      return false;
    }
    if (!this.isRoundingDropdownDisabled && !this.roundingSelection) {
      this.isRoundingDropdownInvalid = true;
      this._alertService.error(
        "Please select an option to multiply values in the document by. If no scaling is needed, select 'Actual'.",
        'Select Multiply By Value');
      return false;
    }
    return true;
  }

  next(): void {
    if (!this._validateBeforeSave()) {
      return;
    }
    this.isCompletingReview = true;
    this.showStatusPopup().then(() => {
      this._executeSave(true, true);
    }).catch(() => {
      this.isLoading = true;
      this._executeSave(true, true);
    });
  }

  saveAndStay(): void {
    this.save();
  }

  handleCellClick(e: MouseEvent, coords){
    if (this.splitscreenOpen && coords['col'] >= 0 && coords['row'] >= 0) {
      this.showSource([{start: coords, end: coords}], false)
    }
  }

  showSource(selection, alertForInvalidCell: boolean = true) {
    this.viewSourcePage = null;
    this.viewSourceBox = null;
    if (!selection?.length) {
      return;
    }
    const hotRow = selection[0].start.row;
    const selectedCol = selection[0].start.col;
    const opts = this.statementOptions[this.activePage];
    const pageModel = this.model.document.pages[this.activePage];
    const metadataLen = pageModel?.metadata?.length;
    const statementOptionsLen = opts?.[0]?.length;
    const numMetadataRows =
      metadataLen ?? statementOptionsLen ?? opts?.[selectedCol]?.length ?? 0;
    const row = hotRow - numMetadataRows;
    const page = this.model.document.pages[this.activePage];
    const pageRow = page?.cells?.[row];

    if (!pageRow) {
      if (alertForInvalidCell) {
        this._alertService.warning('Could not identify source cell or source page.', 'Warning', 5);
      }
      return;
    }

    const columnOrder = this.showSourceColumnOrder(selectedCol, pageRow.length);
    const selectedCell = pageRow[selectedCol];
    if (!selectedCell?.sourceBox?.hasOwnProperty('llc')) {
      return;
    }
    for (const col of columnOrder) {
      const cell = pageRow[col];
      if (cell == null) {
        continue;
      }
      const pageNumber = cell.hasOwnProperty('pageNumber') ? cell.pageNumber : -1;
      if (
        cell.hasOwnProperty('sourceBox') &&
        cell.sourceBox != null &&
        this.sourceBoxIsValid(cell.sourceBox, pageNumber)
      ) {
        this.openSplitscreen();
        this.viewSource = true;
        this.viewSourcePage = cell.sourceBox.pageNumber;
        this.viewSourceBox = cell.sourceBox;
        return;
      }
    }
    for (const col of columnOrder) {
      const cell = pageRow[col];
      if (cell == null) {
        continue;
      }
      const pageNumber = cell.hasOwnProperty('pageNumber') ? cell.pageNumber : -1;
      if (pageNumber > -1) {
        this.openSplitscreen();
        this.viewSource = true;
        this.viewSourcePage = cell.pageNumber;
        this.viewSourceBox = null;
        return;
      }
    }
    if (alertForInvalidCell) {
      this._alertService.warning('Could not identify source cell or source page.', 'Warning', 5);
    }
  }

  private reviewCellDisplayText(cell: any): string {
    if (cell == null) {
      return '';
    }
    if (typeof cell === 'string') {
      return cell.trim();
    }
    if (typeof cell === 'object' && cell.text != null) {
      return String(cell.text).trim();
    }
    return '';
  }

  /**
   * AUTO-4151: the cell's pre-translation text, as every merge path resolves it.
   *
   * The cell's OWN fields outrank `sourceBox.rawText`. A label wrapped across two visual lines is
   * stitched into `rawText`/`text` upstream, but `sourceBox` still belongs to the LAST line only,
   * so its `rawText` holds just the tail ("matures September 2025.") while the cell holds the whole
   * label. Reading the source box first silently dropped the stitched-in prefix when merging rows.
   * It stays as a last resort for cells that carry no raw field of their own.
   */
  private reviewCellRawText(cell: ReviewEditorCell | string | number | null | undefined): string {
    if (!cell || typeof cell !== 'object') {
      return String(cell || '').trim();
    }
    return String(
      (cell.rawText ?? '') ||
      (cell.raw_text ?? '') ||
      (cell.sourceBox?.rawText ?? '')
    ).trim();
  }

  /**
   * True when a cell has no real label text for merge purposes (empty, whitespace, or NBSP-only).
   */
  private isEmptyCellForMerge(cell: any): boolean {
    const t = this.reviewCellDisplayText(cell);
    if (t === '') {
      return true;
    }
    return t.replace(/\u00a0/g, ' ').trim() === '';
  }

  /**
   * Empty merged cells sometimes carry a stray sourceBox on column A (e.g. amount region). Skip it
   * when another merged column has real text so union/fallback does not prefer the wrong region.
   */
  private shouldSkipSourceBoxFromEmptyMergedCell(cellObjects: any[], cell: any): boolean {
    if (cellObjects.length < 2) {
      return false;
    }
    if (!this.isEmptyCellForMerge(cell)) {
      return false;
    }
    return cellObjects.some(
      c => c && typeof c === 'object' && !this.isEmptyCellForMerge(c)
    );
  }

  /**
   * Two-column merge (A+B): when column A is empty (incl. NBSP) and B holds the line, use B's
   * sourceBox on the merged cell — that is the wide label PDF mapping (e.g. x 389–989). After merge,
   * grid column index 1 is the former amount column (narrow x ~2543); it is not “the same” cell as
   * pre-merge column B, which is why those coordinates differ in logs if column 0 lost B's mapping.
   */
  private applyRightSourceWhenLeftEmptyTwoColumnMerge(
    cellObjects: any[],
    mergedSourceBox: any
  ): any {
    if (cellObjects.length !== 2) {
      return mergedSourceBox;
    }
    const left = cellObjects[0];
    const right = cellObjects[1];
    if (!this.isEmptyCellForMerge(left) || this.reviewCellDisplayText(right) === '') {
      return mergedSourceBox;
    }
    if (!right || typeof right !== 'object' || !right.sourceBox) {
      return mergedSourceBox;
    }
    if (this.sourceBoxHasValidGeometry(right.sourceBox)) {
      return right.sourceBox;
    }
    return mergedSourceBox != null ? mergedSourceBox : right.sourceBox;
  }

  private _toWesternNumerals(text: string): string {
    return (text || '').replace(/\p{Nd}/gu, d => {
      const cp = d.codePointAt(0)!;
      let zero = cp;
      while (/\p{Nd}/u.test(String.fromCodePoint(zero - 1))) zero--;
      return String(cp - zero);
    });
  }

  /** True when cell text looks like a numeric amount (used when merging label + value columns). */
  private cellTextLooksLikeAmount(text: string): boolean {
    if (!text || !String(text).trim()) {
      return false;
    }
    const t = String(text).trim().replace(/\s/g, '');
    if (/^\([\d,\.]+\)$/.test(t)) {
      return true;
    }
    return /^-?\$?[\d,\.]+$/.test(t) && /\d/.test(t);
  }

  /**
   * When merging columns, Show Source often falls back from column A to B (value has the PDF box).
   * If both cells have sourceBoxes, union would span label+amount; for label+amount rows we keep the
   * rightmost box so the merged cell matches that fallback. Multi-period merges (two amount columns)
   * still union when both sides look like amounts.
   */
  private shouldPreferRightmostSourceBoxForMerge(cellObjects: any[]): boolean {
    if (cellObjects.length < 2) {
      return false;
    }
    const leftText = this.reviewCellDisplayText(cellObjects[0]);
    const rightText = this.reviewCellDisplayText(cellObjects[cellObjects.length - 1]);
    if (!rightText) {
      return false;
    }
    return !this.cellTextLooksLikeAmount(leftText) && this.cellTextLooksLikeAmount(rightText);
  }

  /** Prefer selected column first, then scan the row (label column often has no sourceBox; value columns hold the PDF link). */
  private showSourceColumnOrder(selectedCol: number, rowLength: number): number[] {
    const indices = Array.from({ length: rowLength }, (_, i) => i);
    if (selectedCol < 0 || selectedCol >= rowLength) {
      return indices;
    }
    return [selectedCol, ...indices.filter(c => c !== selectedCol)];
  }

  sourceBoxIsValid(sourceBox, pageNumber) {
    const hasCoord = (c: { x?: number; y?: number } | null | undefined) =>
      c != null && typeof c.x === 'number' && typeof c.y === 'number';
    if (!sourceBox?.llc || !sourceBox?.lrc || !sourceBox?.ulc || !sourceBox?.urc) {
      return false;
    }
    if (
      !hasCoord(sourceBox.llc) ||
      !hasCoord(sourceBox.lrc) ||
      !hasCoord(sourceBox.ulc) ||
      !hasCoord(sourceBox.urc)
    ) {
      return false;
    }
    return (
      pageNumber == null ||
      pageNumber === -1 ||
      pageNumber === sourceBox?.pageNumber ||
      (pageNumber === 0 && sourceBox?.pageNumber != null)
    );
  }

  /** Geometry only — used when merging columns so cell.pageNumber mismatches do not drop valid sourceBoxes. */
  private sourceBoxHasValidGeometry(sourceBox: any): boolean {
    const hasCoord = (c: { x?: number; y?: number } | null | undefined) =>
      c != null && typeof c.x === 'number' && typeof c.y === 'number';
    return (
      sourceBox?.llc != null &&
      sourceBox?.lrc != null &&
      sourceBox?.ulc != null &&
      sourceBox?.urc != null &&
      hasCoord(sourceBox.llc) &&
      hasCoord(sourceBox.lrc) &&
      hasCoord(sourceBox.ulc) &&
      hasCoord(sourceBox.urc)
    );
  }

  /** Builds a single blank, newly-inserted cell using the predefined new-cell shape. */
  private _buildNewCell(): { text: string; raw_text: string; translated_raw_text: string; isNewcell: boolean; sourceBox: { rawText: string } } {
    return {
      text: '',
      raw_text: '',
      translated_raw_text: '',
      sourceBox: { rawText: '' },
      isNewcell: true
    };
  }

  /** Builds a row of blank, newly-inserted cells (one per column) using the predefined new-cell shape. */
  private _buildNewCellRow(numCol: number): Array<{ text: string; raw_text: string; translated_raw_text: string; isNewcell: boolean }> {
    const newCellRow = [];
    for (let i = 0; i < numCol; i++) {
      newCellRow.push(this._buildNewCell());
    }
    return newCellRow;
  }

  insertRow(selection, insertAbove: boolean) {
    let selectedRowIndex = 0;
    if (insertAbove) {
      selectedRowIndex = selection[0].start.row;
    } else {
      selectedRowIndex = selection[0].end.row + 1;
    }
    const numCol = this.pageData[0].length;

    const arrayToInsert = [];
    for (let i = 1; i <= numCol; i++) {
      arrayToInsert.push('');
    }

    const cellsToInsert = this._buildNewCellRow(numCol);

    this.pageData.splice(selectedRowIndex, 0, arrayToInsert);
    this.pageData = this.pageData.slice(0);

    const numMetadataRows = this.statementOptions[this.activePage][0].length;
    this.model.document.pages[this.activePage]
      .cells.splice(
        selectedRowIndex - numMetadataRows,
        0,
        cellsToInsert
      );

    this.patchActiveStackForRowInsert(selectedRowIndex, numMetadataRows);
  }

  insertFrom(selection, fromPageIndex: number): void {
    const selectedRowIndex = selection[0].end.row + 1;
    const numMetadataRowsBefore = this.model.document.pages[this.activePage].metadata?.length ?? 0;
    let tempPageData;

    // idk why, but there's a bug where pageData first key has many extra columns
    let activePageNumCol;
    if (this.pageData.length > 1) {
      activePageNumCol = this.pageData[1].length;
    } else {
      activePageNumCol = this.pageData[0].length;
    }

    const fromPageNumCol = this.model.document.pages[fromPageIndex].cells[0].length;
    const columnsWidenedBy = Math.max(0, fromPageNumCol - activePageNumCol);
    tempPageData = this.pageData;

    if (activePageNumCol < fromPageNumCol) {
      // activePage has less columns, need to add the difference to activePage and then insert stuff

      const numDifference = fromPageNumCol - activePageNumCol;

      // activePage has less columns, need to add the difference to activePage and then insert stuff
      tempPageData.forEach( (row, index) => {
        for (let i = 0; i < numDifference; i++) {
          tempPageData[index].push('');
        }
      });

      const tempCellsData = JSON.parse(JSON.stringify(this.model.document.pages[this.activePage].cells));

      this.model.document.pages[this.activePage].cells.forEach( (cell, index) => {
        for (let i = 0; i < numDifference; i++) {
          tempCellsData[index].push({ text: '', raw_text: '', translated_raw_text: '' });
        }
      });

      this.model.document.pages[this.activePage].cells = JSON.parse(JSON.stringify(tempCellsData));

      const activeMetadata = this.model.document.pages[this.activePage].metadata;
      if (activeMetadata) {
        activeMetadata.forEach((row) => {
          for (let i = 0; i < numDifference; i++) {
            row.push('');
          }
        });
      }

      activePageNumCol = activePageNumCol + numDifference; // add in columns added to active page
    } else if (activePageNumCol > fromPageNumCol) {
      // activePage has more columns, need to add the difference to incoming data and then insert stuff
      // fromPage.metadata is intentionally not widened here: removeInsertedPage(fromPageIndex) below removes the page after insertTableData, so its metadata is never read again.
      const numDifference = activePageNumCol - fromPageNumCol;

      const tempCellsData = this.model.document.pages[fromPageIndex].cells;

      this.model.document.pages[fromPageIndex].cells.forEach( (cell, index) => {
        for (let i = 0; i < numDifference; i++) {
          tempCellsData[index].push({ text: '', raw_text: '', translated_raw_text: '' });
        }
      });

      this.model.document.pages[fromPageIndex].cells = tempCellsData;
    }

    // Capture the actual inserted content (post-widening) before insertTableData
    // splices it in. Snapshots need this so undo restores the imported content,
    // not empty placeholder rows.
    const sourceCells = this.model.document.pages[fromPageIndex].cells;
    const insertedCellRows: any[][] = JSON.parse(JSON.stringify(sourceCells));
    const insertedPageDataRows: any[][] = sourceCells.map((row: any[]) => {
      const arr: any[] = [];
      for (let i = 0; i < activePageNumCol; i++) {
        arr.push(row[i]?.text ?? '');
      }
      return arr;
    });

    this.insertTableData(tempPageData, fromPageIndex, activePageNumCol, selectedRowIndex);
    this._setPageTabularData();
    this.patchActiveStackForInsertFrom(
      selectedRowIndex,
      columnsWidenedBy,
      numMetadataRowsBefore,
      insertedCellRows,
      insertedPageDataRows,
    );
    this.removeInsertedPage(fromPageIndex);
    return;
  }


  insertTableData(pageData, dataToPastePageIndex: number, activePageNumCol: number, selectedRowIndex: number): void {
    // we currently refer to this data in many locations as "pageData" or as "pages"
    // but each tab at the bottom is actually a table instead of a page
    // we will start to rename related variables as "tables" instead of "pages" as we move forward

    const tempPageData = JSON.parse(JSON.stringify(pageData));

    const tempModelCells = JSON.parse(JSON.stringify(this.model.document.pages[this.activePage].cells));

    const numMetaFields = this.model.document.pages[this.activePage].metadata.length;

    this.model.document.pages[dataToPastePageIndex].cells.forEach( (cell, index) => {

      const modelCellsKey = selectedRowIndex + index - numMetaFields;
      const pageDataKey = selectedRowIndex + index;

      tempModelCells.splice(modelCellsKey, 0, cell);

      const arrayToInsert = [];

      for (let i = 0; i < activePageNumCol; i++) {
        arrayToInsert.push(cell[i].text);
      }

      tempPageData.splice(pageDataKey, 0, arrayToInsert);
    });

    while (this.statementOptions[this.activePage].length < this.statementOptions[dataToPastePageIndex].length) {
      this.statementOptions[this.activePage].push(
          JSON.parse(JSON.stringify(this._environmentService.getStatementOptions()))
        )
    }

    this.model.document.pages[this.activePage].cells =  JSON.parse(JSON.stringify(tempModelCells));

    this.pageData = tempPageData;

  }

  removeInsertedPage(pageIndex: number): void {
    this.deletePage(pageIndex, true); // delete page w/o override confirmation
  }

  reloadTable(): void {
    this.pageData = this.pageData.slice(0);
  }

  insertColumn(selection, insertLeft) {
    // A new page that is created does not populate with statement options so we cannot rely on the object
    // Since statement options don't exist yet as the initial population is done via the add statement options button
    // we can ignore the populating in this case. All others need the statement options
    if (this.statementOptions[this.activePage].length !== 0) {
      this.statementOptions[this.activePage].push( // first we add options so we don't error later
        JSON.parse(JSON.stringify(this._environmentService.getStatementOptions()))
      );
    }

    let selectedRowIndex = 0;
    if (insertLeft) {
      selectedRowIndex = selection[0].start.col;
    } else {
      selectedRowIndex = selection[0].end.col + 1;
    }

    const grid = [];
    this.pageData.forEach(row => {
      row.splice(selectedRowIndex, 0, '')
      grid.push(row.slice(0));
    });
    this.pageData = grid;

    const cells = [];
    this.model.document.pages[this.activePage].cells.forEach(row => {
      row.splice(selectedRowIndex, 0, {
        text: '',
        raw_text: '',
        translated_raw_text: '',
        sourceBox: { rawText: '' },
        isNewcell: true
      });
      cells.push(row.slice(0));
    });
    this.model.document.pages[this.activePage].cells = cells;

    this.reloadTableData();

    this.patchActiveStackForColumnInsert(selectedRowIndex);
  }

  // suppressUndo=true: redo path; skips snapshot+push.
  removeRows(selection, suppressUndo: boolean = false): void {
    const snapshot = suppressUndo ? null : this.captureSnapshot();

    selection.sort((a, b) => (a.start.row > b.start.row) ? -1 : 1 )

    const mergedSelections = [];
    selection.forEach((s, idx: number) => {
      if (idx === 0) {
        mergedSelections.push(s);
      } else if (selectionsOverlap(mergedSelections[mergedSelections.length - 1], s)) {
        mergedSelections[mergedSelections.length - 1] = mergeSelections(mergedSelections[mergedSelections.length - 1], s);
      } else {
        mergedSelections.push(s);
      }
    });
    mergedSelections.forEach(el => {
      // If it's the same, we still want to remove 1.
      const rowsToRemove = (el.end.row - el.start.row) + 1;

      this.pageData.splice(el.start.row, rowsToRemove);
      this.pageData = this.pageData.slice(0);

      const numMetadataRows = this.statementOptions[this.activePage][0].length;

      this.model.document.pages[this.activePage].cells.splice(
        el.start.row - numMetadataRows,
        rowsToRemove
      );

      this.model.document.pages[this.activePage].cells = JSON.parse(JSON.stringify(this.model.document.pages[this.activePage].cells));
    });
    // AUTO-2822: matches mergeRows at :1779. Defensive — Handsontable's row-shrink
    // re-render appears to clean up stale entries in practice, but relying on that
    // is fragile across HT versions. See removeColumns for full rationale.
    this.validatePage(this.activePage);
    this.cellErrorsFromBackendValidation.clearPage(this.activePage);

    if (snapshot) {
      const selectionRef: SelectionRef = { selection: JSON.parse(JSON.stringify(selection)) };
      this.recordUndoableAction(
        snapshot,
        REVIEW_ACTION_TYPES.REMOVE_ROWS,
        () => this.removeRows(selectionRef.selection, true),
        selectionRef,
      );
    }
  }

  mergeColumnsDisabled(): boolean {
    if (!this.handsOnTableInstance) {
      return true;
    }
    const selectedRange = this.handsOnTableInstance.getSelected();
    const ranges = this.selectionRangesFromHandsontable(selectedRange);
    const span = this.columnMergeSpanFromSelections(ranges);
    if (!span) {
      return true;
    }
    if (!this.selectedColumnsFormRectangle(ranges)) {
      return true;
    }
    if (this.hasMetadataOnlyRange(ranges)) {
      return true;
    }
    if (this.selectedRowsAreOnlyMetadata(ranges)) {
      return true;
    }
    return span.endCol <= span.startCol;
  }

  private selectedRowsAreOnlyMetadata(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): boolean {
    const span = this.rowMergeSpanFromSelections(selections);
    if (!span) {
      return false;
    }
    return span.endRow <= MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX;
  }

  private selectedColumnsFormRectangle(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): boolean {
    const columnsByRow = this.selectedColumnsByRow(selections);
    if (columnsByRow.size === 0) {
      return false;
    }
    let rectangleSignature: string | null = null;
    for (const cols of columnsByRow.values()) {
      if (cols.length < 2) {
        return false;
      }
      const isContiguous = cols[cols.length - 1] - cols[0] === cols.length - 1;
      if (!isContiguous) {
        return false;
      }
      const rowSignature = `${cols[0]}-${cols[cols.length - 1]}`;
      if (rectangleSignature === null) {
        rectangleSignature = rowSignature;
      } else if (rowSignature !== rectangleSignature) {
        return false;
      }
    }
    return true;
  }

  private hasMetadataOnlyRange(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): boolean {
    if (!selections?.length) {
      return false;
    }
    const metadataRowCount = this.model.document.pages[this.activePage]?.metadata?.length ?? 0;
    if (metadataRowCount <= 0) {
      return false;
    }
    const lastMetadataRow = metadataRowCount - 1;
    return selections.some(s => {
      const startRow = Math.min(s.start.row, s.end.row);
      const endRow = Math.max(s.start.row, s.end.row);
      return startRow >= 0 && endRow <= lastMetadataRow;
    });
  }
  
  mergeRowsDisabled(): boolean {
    if (!this.handsOnTableInstance) {
      return true;
    }
    const ranges = this.selectionRangesFromHandsontable(this.handsOnTableInstance.getSelected());
    return !this.canMergeRows(ranges);
  }

  private canMergeRows(
    ranges: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): boolean {
    const span = this.rowMergeSpanFromSelections(ranges);
    if (!span) {
      return false;
    }
    if (span.endRow <= span.startRow) {
      return false;
    }
    if (!this.rowSelectionIsContiguous(ranges)) {
      return false;
    }
    return span.startRow > MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX;
  }

  private rowSelectionIsContiguous(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): boolean {
    if (selections.length <= 1) {
      return true;
    }
    const sorted = selections
      .map(s => ({
        startRow: Math.min(s.start.row, s.end.row),
        endRow: Math.max(s.start.row, s.end.row),
      }))
      .sort((a, b) => a.startRow - b.startRow);
    let coveredEnd = sorted[0].endRow;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].startRow > coveredEnd + 1) {
        return false;
      }
      coveredEnd = Math.max(coveredEnd, sorted[i].endRow);
    }
    return true;
  }

  // suppressUndo=true: redo path; skips snapshot+push and uses the historical selection.
  mergeColumns(selection: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>, suppressUndo: boolean = false): void {
    const fromHot = this.selectionRangesFromHandsontable(this.handsOnTableInstance?.getSelected());
    const effective = suppressUndo ? selection : (fromHot.length ? fromHot : selection);
    if (!this.selectedColumnsFormRectangle(effective)) {
      return;
    }
    const span = this.columnMergeSpanFromSelections(effective);
    if (!span || span.endCol <= span.startCol) {
      return;
    }

    const snapshot = suppressUndo ? null : this.captureSnapshot();

    if (this.isFullColumnSelection(effective)) {
      this.mergeFullColumns(span);
    } else {
      this.mergePartialColumns(effective);
    }

    this.pageData = this.pageData.slice(0);
    this.reloadTableData();
    // AUTO-2822: matches mergeRows at :1779. See removeColumns for full rationale.
    this.validatePage(this.activePage);
    this.cellErrorsFromBackendValidation.clearPage(this.activePage);

    if (snapshot) {
      const selectionRef: SelectionRef = { selection: JSON.parse(JSON.stringify(selection)) };
      this.recordUndoableAction(
        snapshot,
        REVIEW_ACTION_TYPES.MERGE_COLUMNS,
        () => this.mergeColumns(selectionRef.selection, true),
        selectionRef,
      );
    }
  }

  private mergeFullColumns(span: { startCol: number; endCol: number }): void {
    const startCol = span.startCol;
    const endCol = span.endCol;
    const numColsToMerge = endCol - startCol + 1;

    this.mergePageDataColumns(startCol, numColsToMerge);
    this.mergeCellsColumns(startCol, numColsToMerge);
    this.spliceStatementOptionsColumns(startCol, numColsToMerge);
    this.spliceMetadataColumns(startCol, numColsToMerge);
  }

  private mergePartialColumns(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): void {
    const columnsByRow = this.selectedColumnsByRow(selections);
    columnsByRow.forEach((cols, rowIndex) => {
      if (cols.length < 2) {
        return;
      }
      this.mergeSelectedCellsInRow(rowIndex, cols);
    });
  }

  private isFullColumnSelection(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): boolean {
    if (!this.pageData.length) {
      return false;
    }
    let minRow = Infinity;
    let maxRow = -Infinity;
    for (const s of selections) {
      minRow = Math.min(minRow, s.start.row, s.end.row);
      maxRow = Math.max(maxRow, s.start.row, s.end.row);
    }
    return minRow <= 0 && maxRow >= this.pageData.length - 1;
  }

  private selectedColumnsByRow(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): Map<number, number[]> {
    const columnsByRow = new Map<number, Set<number>>();
    for (const s of selections) {
      const startRow = Math.min(s.start.row, s.end.row);
      const endRow = Math.max(s.start.row, s.end.row);
      const startCol = Math.min(s.start.col, s.end.col);
      const endCol = Math.max(s.start.col, s.end.col);
      for (let row = startRow; row <= endRow; row += 1) {
        const cols = columnsByRow.get(row) ?? new Set<number>();
        for (let col = startCol; col <= endCol; col += 1) {
          cols.add(col);
        }
        columnsByRow.set(row, cols);
      }
    }
    const sorted = new Map<number, number[]>();
    columnsByRow.forEach((cols, row) => {
      sorted.set(row, Array.from(cols).sort((a, b) => a - b));
    });
    return sorted;
  }

  private mergeSelectedCellsInRow(rowIndex: number, sortedCols: number[]): void {
    const leftCol = sortedCols[0];
    this.mergePageDataRowCells(rowIndex, leftCol, sortedCols);
    this.mergeModelRowCells(rowIndex, leftCol, sortedCols);
  }

  private mergePageDataRowCells(rowIndex: number, leftCol: number, sortedCols: number[]): void {
    const row = this.pageData[rowIndex];
    if (!row) {
      return;
    }
    if (rowIndex > MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX) {
      const modelRowIndex = this.modelCellsRowIndex(rowIndex);
      const modelRow = modelRowIndex == null
        ? null
        : this.model.document.pages[this.activePage]?.cells?.[modelRowIndex] ?? null;
      const merged = this.mergedDisplayTextForRow(row, modelRow, leftCol, sortedCols.length);
      row[leftCol] = merged !== '' ? merged : (row[leftCol] ?? '');
    }
    for (const col of sortedCols) {
      if (col !== leftCol) {
        row[col] = '';
      }
    }
  }

  private mergeModelRowCells(rowIndex: number, leftCol: number, sortedCols: number[]): void {
    const modelRowIndex = this.modelCellsRowIndex(rowIndex);
    if (modelRowIndex == null) {
      return;
    }
    const page = this.model.document.pages[this.activePage];
    const modelRow = page?.cells?.[modelRowIndex];
    if (!modelRow) {
      return;
    }
    const selectedCells = sortedCols.map(col => modelRow[col]);
    modelRow[leftCol] = this.mergeCellObjects(selectedCells, this.isArabicPage(page) && leftCol > 0);
    if (leftCol > 0 && rowIndex > MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX) {
      this.applyConcatenatedNumericToMergedCell(
        modelRow[leftCol],
        this.pageData[rowIndex]?.[leftCol],
        this.isArabicPage(page)
      );
    }
    for (const col of sortedCols) {
      if (col !== leftCol) {
        modelRow[col] = { text: '', raw_text: '', translatedRawText: '', sourceBox: { rawText: '' },  value: null          
        };
      }
    }
  }

  private modelCellsRowIndex(pageDataRowIndex: number): number | null {
    const cells = this.model.document.pages[this.activePage]?.cells || [];
    const firstRowFirstCell = cells[0]?.[0];
    const cellsEmbedMetadataRowsAlignedWithPageData =
      cells.length === this.pageData.length &&
      MANUAL_REVIEW_HEADERS_INCLUDING_LEGACY.includes(this.cellTextForMetadataCheck(firstRowFirstCell));

    if (cellsEmbedMetadataRowsAlignedWithPageData) {
      return pageDataRowIndex < cells.length ? pageDataRowIndex : null;
    }
    if (pageDataRowIndex <= MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX) {
      return null;
    }
    const cellIndex = pageDataRowIndex - (MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX + 1);
    return cellIndex >= 0 && cellIndex < cells.length ? cellIndex : null;
  }

  // suppressUndo=true: redo path; skips snapshot+push and uses the historical selection.
  mergeRows(selection: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>, suppressUndo: boolean = false): void {
    const fromHot = this.selectionRangesFromHandsontable(this.handsOnTableInstance?.getSelected());
    const effective = suppressUndo ? selection : (fromHot.length ? fromHot : selection);
    if (!this.canMergeRows(effective)) {
      return;
    }
    const span = this.rowMergeSpanFromSelections(effective)!;
    const lastCol = (this.pageData[span.startRow]?.length ?? 0) - 1;
    const fullRow = span.startCol === 0 && span.endCol >= lastCol;

    const ctx = this.buildRowMergeContext(span, fullRow);
    if (!ctx) {
      return;
    }

    const snapshot = suppressUndo ? null : this.captureSnapshot();

    this.mergePageDataRows(span, fullRow, ctx);
    this.mergeCellsRows(span, fullRow, ctx);

    this.pageData = this.pageData.slice(0);
    this.reloadTableData();
    this.validatePage(this.activePage);
    // AUTO-2822: also nuke backend-store entries whose coord positions may have
    // shifted after the merge. Symmetric with removeRows/removeColumns/mergeColumns.
    this.cellErrorsFromBackendValidation.clearPage(this.activePage);

    if (snapshot) {
      const selectionRef: SelectionRef = { selection: JSON.parse(JSON.stringify(selection)) };
      this.recordUndoableAction(
        snapshot,
        REVIEW_ACTION_TYPES.MERGE_ROWS,
        () => this.mergeRows(selectionRef.selection, true),
        selectionRef,
      );
    }
  }

  private selectionRangesFromHandsontable(
    selected: Array<[number, number, number, number]> | undefined | null
  ): Array<{ start: { row: number; col: number }; end: { row: number; col: number } }> {
    if (!selected?.length) {
      return [];
    }
    return selected
      .filter((item): item is [number, number, number, number] => Array.isArray(item) && item.length >= 4)
      .map(item => {
        const r0 = item[0];
        const c0 = Math.max(0, item[1]);
        const r1 = item[2];
        const c1 = Math.max(0, item[3]);
        return {
          start: { row: Math.min(r0, r1), col: Math.min(c0, c1) },
          end: { row: Math.max(r0, r1), col: Math.max(c0, c1) },
        };
      });
  }

  private columnMergeSpanFromSelections(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): { startCol: number; endCol: number } | null {
    if (!selections?.length) {
      return null;
    }
    let startCol = Infinity;
    let endCol = -Infinity;
    for (const s of selections) {
      const c0 = Math.min(s.start.col, s.end.col);
      const c1 = Math.max(s.start.col, s.end.col);
      startCol = Math.min(startCol, c0);
      endCol = Math.max(endCol, c1);
    }
    if (startCol === Infinity) {
      return null;
    }
    return { startCol, endCol };
  }

  private rowMergeSpanFromSelections(
    selections: Array<{ start: { row: number; col: number }; end: { row: number; col: number } }>
  ): { startRow: number; endRow: number; startCol: number; endCol: number } | null {
    if (!selections?.length) {
      return null;
    }
    let startRow = Infinity;
    let endRow = -Infinity;
    let startCol = Infinity;
    let endCol = -Infinity;
    for (const s of selections) {
      const r0 = Math.min(s.start.row, s.end.row);
      const r1 = Math.max(s.start.row, s.end.row);
      const c0 = Math.min(s.start.col, s.end.col);
      const c1 = Math.max(s.start.col, s.end.col);
      startRow = Math.min(startRow, r0);
      endRow = Math.max(endRow, r1);
      startCol = Math.min(startCol, c0);
      endCol = Math.max(endCol, c1);
    }
    if (startRow === Infinity) {
      return null;
    }
    return { startRow, endRow, startCol, endCol };
  }

  private mergePageDataColumns(startCol: number, numColsToMerge: number): void {
    const page = this.model.document.pages[this.activePage];
    const cells = page?.cells || [];
    const firstRowFirstCell = cells[0]?.[0];
    const cellsEmbedMetadataRowsAlignedWithPageData =
      cells.length === this.pageData.length &&
      MANUAL_REVIEW_HEADERS_INCLUDING_LEGACY.includes(this.cellTextForMetadataCheck(firstRowFirstCell));

    this.pageData.forEach((row, rowIndex) => {
      if (rowIndex <= MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX) {
        const v = row[startCol];
        const combined = v != null && String(v).trim() !== '' ? String(v).trim() : '';
        row[startCol] = combined !== '' ? combined : (row[startCol] ?? '');
        for (let i = 0; i < numColsToMerge - 1; i++) {
          row.splice(startCol + 1, 1);
        }
        return;
      }

      let modelRow: any[] | null = null;
      if (cellsEmbedMetadataRowsAlignedWithPageData) {
        modelRow = cells[rowIndex] || null;
      } else {
        const cellIndex = rowIndex - (MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX + 1);
        modelRow = (cellIndex >= 0 ? cells[cellIndex] : null) || null;
      }

      const merged = this.mergedDisplayTextForRow(row, modelRow, startCol, numColsToMerge);
      row[startCol] = merged !== '' ? merged : (row[startCol] ?? '');
      for (let i = 0; i < numColsToMerge - 1; i++) {
        row.splice(startCol + 1, 1);
      }
    });
  }

  /**
   * True when the page's detected language is Arabic. Matches the bare `ar` code as well as
   * region/script variants (`ar-SA`, `ar_EG`, `AR`), tolerating casing and stray whitespace from
   * upstream OCR/translation services, without matching unrelated codes such as `arc` (Aramaic).
   */
  private isArabicPage(page: any): boolean {
    const language = page?.detectedLanguage?.language;
    if (typeof language !== 'string') {
      return false;
    }
    const normalized = language.trim().toLowerCase();
    return normalized === 'ar' || normalized.startsWith('ar-') || normalized.startsWith('ar_');
  }

  private mergedDisplayTextForRow(
    pageRow: any[],
    modelRow: any[] | null,
    startCol: number,
    numCols: number
  ): string {
    const getOriginalText = (c: any): string => this.reviewCellRawText(c);

    const isArabic = this.isArabicPage(this.model?.document?.pages?.[this.activePage]);

    const originalParts: string[] = [];
    const translatedParts: string[] = [];

    if (modelRow) {
      for (let c = startCol; c < startCol + numCols && c < pageRow.length; c++) {
        const cell = modelRow[c];
        const displayValue = pageRow[c];
        if (!cell || (typeof cell !== 'object' && String(cell).trim() === '')) continue;
        if (displayValue == null || String(displayValue).trim() === '') continue;
        if (typeof cell === 'object' && cell.value != null) {
          const translatedNum = isArabic && startCol > 0
            ? String(cell.value)
            : (cell.translatedRawText || cell.translated_raw_text || '').trim();
          const rawNum = getOriginalText(cell);
          originalParts.push(rawNum || translatedNum || String(cell.value));
          translatedParts.push(translatedNum || String(cell.value));
          continue;
        }
        const orig = getOriginalText(cell);
        const tr = (typeof cell === 'object'
          ? (cell.translatedRawText || cell.translated_raw_text)
          : '') || '';
        const display = orig || (typeof cell === 'object' ? cell.text : String(cell));
        if (display && display.trim() !== '') originalParts.push(display.trim());
        const cellText = (typeof cell === 'object' ? (cell.text || '') : String(cell)).trim();
        const translatedDisplay = (tr && tr.trim()) || cellText || this._toWesternNumerals(display && display.trim() || '');
        if (translatedDisplay) translatedParts.push(translatedDisplay);
      }
    }

    if (originalParts.length === 0) {
      return pageRow[startCol] != null ? String(pageRow[startCol]) : '';
    }
    const originalText = originalParts.join(' ');
    const translatedText = translatedParts.join(' ');
    if (startCol > 0) {
      // Value-only columns: concatenate OCR-split numbers into one value, else space-join.
      // Always Western numerals, regardless of the language toggle.
      return this.joinValueColumnParts(originalParts, translatedParts);
    }
    // Label column involved: respect the language toggle
    return this.viewInNativeLanguage ? originalText : (translatedText || originalText);
  }

  private buildRowMergeContext(
    span: { startRow: number; endRow: number; startCol: number; endCol: number },
    fullRow: boolean
  ): {
    offset: number;
    effectiveStartCol: number;
    effectiveEndCol: number;
    numRowsToMerge: number;
    cellsStartRow: number;
    cellsEndRow: number;
    isEmptyAtPageRow: (r: number, c: number) => boolean;
  } | null {
    const page = this.model.document.pages[this.activePage];
    const cells = page?.cells || [];
    const numMetadataRows = page?.metadata?.length
      ?? (MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX + 1);
    const cellsEmbedMetadataAligned = cells.length === this.pageData.length;
    const offset = cellsEmbedMetadataAligned ? 0 : numMetadataRows;

    const cellsStartRow = span.startRow - offset;
    const cellsEndRow = span.endRow - offset;
    if (cellsStartRow < 0 || cellsEndRow < cellsStartRow || !cells[cellsStartRow]) {
      this.logger?.info?.('mergeRows aborted: row span maps outside cells matrix', {
        span,
        offset,
        cellsLength: cells.length,
        pageDataLength: this.pageData.length,
      });
      return null;
    }

    const lastCol = (this.pageData[span.startRow]?.length
      ?? cells[cellsStartRow]?.length
      ?? 0) - 1;
    const effectiveStartCol = fullRow ? 0 : Math.max(0, span.startCol);
    const effectiveEndCol = fullRow ? lastCol : Math.min(lastCol, span.endCol);
    const numRowsToMerge = cellsEndRow - cellsStartRow + 1;

    const emptyKey = (r: number, c: number) => `${r}_${c}`;
    const emptySnapshot: { [key: string]: boolean } = {};
    for (let r = span.startRow; r <= span.endRow; r++) {
      for (let c = effectiveStartCol; c <= effectiveEndCol; c++) {
        emptySnapshot[emptyKey(r, c)] = this.isDisplayCellEmpty(r, c);
      }
    }
    const isEmptyAtPageRow = (r: number, c: number) => emptySnapshot[emptyKey(r, c)] ?? true;

    return {
      offset,
      effectiveStartCol,
      effectiveEndCol,
      numRowsToMerge,
      cellsStartRow,
      cellsEndRow,
      isEmptyAtPageRow,
    };
  }

  private isDisplayCellEmpty(r: number, c: number): boolean {
    const v = this.pageData[r]?.[c];
    return v == null || (typeof v === 'string' && v.trim() === '');
  }

  private extractRowMergePartsFromCell(
    target: ReviewEditorCell | string | number | null | undefined,
    preferValueAsTranslation = false
  ): { original: string | null; translated: string | null; isNumeric: boolean } | null {
    if (target == null) {
      return null;
    }
    if (typeof target !== 'object') {
      const s = String(target).trim();
      return s === '' ? null : { original: s, translated: s, isNumeric: false };
    }

    const rawSource = this.reviewCellRawText(target);

    if (target.value != null) {
      const translatedNum = preferValueAsTranslation
        ? String(target.value)
        : (target.translatedRawText || target.translated_raw_text || '').trim();
      return {
        original: rawSource || translatedNum || String(target.value),
        translated: translatedNum || String(target.value),
        isNumeric: true,
      };
    }

    const tr = (target.translatedRawText || target.translated_raw_text || '').trim();
    const cellText = (target.text != null ? String(target.text) : '').trim();
    const display = (rawSource || cellText).trim();
    const translatedDisplay = tr || cellText || this._toWesternNumerals(display);
    if (display === '' && cellText === '' && tr === '') {
      return null;
    }
    return {
      original: display !== '' ? display : null,
      translated: translatedDisplay || null,
      isNumeric: false,
    };
  }

  private mergePageDataRows(
    span: { startRow: number; endRow: number; startCol: number; endCol: number },
    fullRow: boolean,
    ctx: { offset: number; effectiveStartCol: number; effectiveEndCol: number; numRowsToMerge: number; isEmptyAtPageRow: (r: number, c: number) => boolean }
  ): void {
    const page = this.model.document.pages[this.activePage];
    const cells = page?.cells || [];
    const isArabic = this.isArabicPage(page);

    for (let c = ctx.effectiveStartCol; c <= ctx.effectiveEndCol; c++) {
      const originalParts: string[] = [];
      const translatedParts: string[] = [];

      for (let r = span.startRow; r <= span.endRow; r++) {
        if (ctx.isEmptyAtPageRow(r, c)) {
          continue;
        }
        const cellsRow = r - ctx.offset;
        const cell = cellsRow >= 0 ? cells[cellsRow]?.[c] : undefined;
        const target = (cell !== undefined && cell !== null) ? cell : this.pageData[r]?.[c];
        const parts = this.extractRowMergePartsFromCell(target, isArabic && c > 0);
        if (!parts) {
          continue;
        }
        if (parts.original) {
          originalParts.push(parts.original);
        }
        if (parts.translated) {
          translatedParts.push(parts.translated);
        }
      }

      let merged: string;
      if (originalParts.length > 0) {
        if (c > 0) {
          merged = this.joinValueColumnParts(originalParts, translatedParts);
        } else {
          const originalText = originalParts.join(' ');
          const translatedText = translatedParts.join(' ');
          merged = this.viewInNativeLanguage ? originalText : (translatedText || originalText);
        }
      } else {
        const existing = this.pageData[span.startRow]?.[c];
        merged = existing != null ? String(existing) : '';
      }
      if (this.pageData[span.startRow]) {
        this.pageData[span.startRow][c] = merged !== '' ? merged : (this.pageData[span.startRow][c] ?? '');
      }
    }

    if (fullRow) {
      this.pageData.splice(span.startRow + 1, ctx.numRowsToMerge - 1);
    } else {
      for (let r = span.startRow + 1; r <= span.endRow; r++) {
        if (!this.pageData[r]) continue;
        for (let c = ctx.effectiveStartCol; c <= ctx.effectiveEndCol; c++) {
          this.pageData[r][c] = '';
        }
      }
    }
  }

  private cellTextForMetadataCheck(cell: any): string {
    if (cell == null) {
      return '';
    }
    if (typeof cell === 'string') {
      return String(cell).trim();
    }
    if (typeof cell === 'object' && cell.text != null) {
      return String(cell.text).trim();
    }
    return '';
  }

  private mergeCellsColumns(startCol: number, numColsToMerge: number): void {
    const page = this.model.document.pages[this.activePage];
    if (!page || !page.cells) {
      return;
    }
    const cells = page.cells;
    const isArabic = this.isArabicPage(page);
    const firstRowFirstCell = cells[0]?.[0];
    const cellsEmbedMetadataRowsAlignedWithPageData =
      cells.length === this.pageData.length &&
      MANUAL_REVIEW_HEADERS_INCLUDING_LEGACY.includes(this.cellTextForMetadataCheck(firstRowFirstCell));

    for (let r = 0; r < cells.length; r++) {
      const row = cells[r];
      if (
        cellsEmbedMetadataRowsAlignedWithPageData &&
        r <= MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX
      ) {
        const keepCell = row[startCol];
        row.splice(startCol, numColsToMerge, keepCell);
        continue;
      }
      const toMerge = row.slice(startCol, startCol + numColsToMerge);
      const mergedCell = this.mergeCellObjects(toMerge, isArabic && startCol > 0);
      if (startCol > 0) {
        const pageDataRowIndex = cellsEmbedMetadataRowsAlignedWithPageData
          ? r
          : r + (MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX + 1);
        this.applyConcatenatedNumericToMergedCell(
          mergedCell,
          this.pageData[pageDataRowIndex]?.[startCol],
          isArabic
        );
      }
      row.splice(startCol, numColsToMerge, mergedCell);
    }
  }

  private mergeCellsRows(
    span: { startRow: number; endRow: number; startCol: number; endCol: number },
    fullRow: boolean,
    ctx: { offset: number; effectiveStartCol: number; effectiveEndCol: number; numRowsToMerge: number; cellsStartRow: number; cellsEndRow: number; isEmptyAtPageRow: (r: number, c: number) => boolean }
  ): void {
    const page = this.model.document.pages[this.activePage];
    if (!page || !page.cells) {
      return;
    }
    const cells = page.cells;
    const isArabic = this.isArabicPage(page);

    for (let c = ctx.effectiveStartCol; c <= ctx.effectiveEndCol; c++) {
      const toMerge: any[] = [];
      for (let r = ctx.cellsStartRow; r <= ctx.cellsEndRow; r++) {
        const pageDataRow = r + ctx.offset;
        toMerge.push(ctx.isEmptyAtPageRow(pageDataRow, c) ? undefined : cells[r]?.[c]);
      }
      const merged = this.mergeCellObjects(toMerge, isArabic && c > 0);
      if (c > 0) {
        this.applyConcatenatedNumericToMergedCell(merged, this.pageData[span.startRow]?.[c], isArabic);
      }
      cells[ctx.cellsStartRow][c] = merged;
    }

    if (fullRow) {
      cells.splice(ctx.cellsStartRow + 1, ctx.numRowsToMerge - 1);
    } else {
      for (let r = ctx.cellsStartRow + 1; r <= ctx.cellsEndRow; r++) {
        if (!cells[r]) continue;
        for (let c = ctx.effectiveStartCol; c <= ctx.effectiveEndCol; c++) {
          cells[r][c] = { text: '', rawText: '', raw_text: '' };
        }
      }
    }

    this.model.document.pages[this.activePage].cells = cells.slice();
  }

  private applyConcatenatedNumericToMergedCell(merged: any, pageDataValue: unknown, isArabic: boolean): void {
    if (!merged || typeof merged !== 'object') {
      return;
    }
    const concatText = pageDataValue != null ? String(pageDataValue).trim() : '';
    if (concatText === '') {
      return;
    }
    const numericValue = this.parseConcatenatedNumeric(concatText);
    if (numericValue === null) {
      return;
    }
    if (!isArabic) {
      merged.text = concatText;
      merged.rawText = concatText;
      merged.raw_text = concatText;
    }
    if (merged.translatedRawText !== undefined) {
      merged.translatedRawText = concatText;
    }
    if (merged.translated_raw_text !== undefined) {
      merged.translated_raw_text = concatText;
    }
    if (merged.sourceBox && merged.sourceBox.rawText !== undefined && !isArabic) {
      merged.sourceBox.rawText = concatText;
    }
    merged.value = numericValue;
  }

  private parseConcatenatedNumeric(text: string): number | null {
    if (text == null) {
      return null;
    }
    const str = String(text);
    if (/\p{L}/u.test(str)) {
      return null;
    }
    const cleaned = str.replace(/[^\d.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
      return null;
    }
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Joins the value-column pieces of a merge the same way for row and column merges. When the
   * pieces concatenate (no separator) into a single valid number they are kept joined — this
   * reconstructs a number that OCR split across cells (e.g. '1,234' + '567' -> '1,234567').
   * Otherwise they are space-separated (e.g. distinct '100' and 'N/A'). Always Western numerals.
   */
  private joinValueColumnParts(originalParts: string[], translatedParts: string[]): string {
    const sourceParts = translatedParts.length ? translatedParts : originalParts;
    const noSpaceConcat = this._toWesternNumerals(sourceParts.join(''));
    return this.parseConcatenatedNumeric(noSpaceConcat) !== null
      ? noSpaceConcat
      : this._toWesternNumerals(sourceParts.join(' '));
  }

  private mergeCellObjects(cellObjects: any[], preferValueAsTranslation = false): any {
    const allFootnotes: any[] = [];
    let mergedSourceBox: any = null;
    let pageNumber: number | undefined;

    for (const cell of cellObjects) {
      if (cell && typeof cell === 'object') {
        if (cell.footnotes && Array.isArray(cell.footnotes)) {
          allFootnotes.push(...cell.footnotes);
        }
        if (pageNumber === undefined && cell.pageNumber != null) {
          pageNumber = cell.pageNumber;
        }
      }
    }

    const validGeometryBoxes: any[] = [];
    for (const cell of cellObjects) {
      if (!cell || typeof cell !== 'object' || !cell.sourceBox) {
        continue;
      }
      if (this.shouldSkipSourceBoxFromEmptyMergedCell(cellObjects, cell)) {
        continue;
      }
      if (this.sourceBoxHasValidGeometry(cell.sourceBox)) {
        validGeometryBoxes.push(cell.sourceBox);
      }
    }
    if (validGeometryBoxes.length === 0) {
      // leave mergedSourceBox null; fallback below
    } else if (validGeometryBoxes.length === 1) {
      mergedSourceBox = validGeometryBoxes[0];
    } else {
      mergedSourceBox = validGeometryBoxes.reduce((acc, box) =>
        acc ? this.unionSourceBox(acc, box) : box
      );
    }
    if (!mergedSourceBox) {
      const cellsWithBox = cellObjects.filter(
        c => c && typeof c === 'object' && c.sourceBox
      );
      const nonEmptyWithBox = cellsWithBox.filter(
        c => this.reviewCellDisplayText(c) !== ''
      );
      const ordered = nonEmptyWithBox.length ? nonEmptyWithBox : cellsWithBox;
      if (ordered.length) {
        mergedSourceBox = ordered[0].sourceBox;
      }
    }

    mergedSourceBox = this.applyRightSourceWhenLeftEmptyTwoColumnMerge(
      cellObjects,
      mergedSourceBox
    );
    mergedSourceBox = this.ensureSourceBoxHasPageSize(mergedSourceBox, cellObjects);

    const getOriginalText = (c: any): string => this.reviewCellRawText(c);

    const rawTextParts: string[] = [];
    const textParts: string[] = [];
    const translatedParts: string[] = [];
    let mergedValue: number | null = null;
    let hasNonNumericTextCell = false;

    for (const c of cellObjects) {
      const orig = getOriginalText(c);
      if (orig !== '') rawTextParts.push(orig);

      // `text` can be absent on a cell; reading it bare throws.
      const cellText = String((c && typeof c === 'object' ? c.text : c) ?? '').trim();
      if (cellText !== '') textParts.push(cellText);

      const isNumericCell = c && typeof c === 'object' && c.value != null;
      if (isNumericCell) {
        if (mergedValue === null) mergedValue = c.value;
      } else if (orig || cellText) {
        hasNonNumericTextCell = true;
      }

      const tr = (c && typeof c === 'object'
        ? (c.translatedRawText || c.translated_raw_text)
        : '') || '';
      const numericFallback = isNumericCell ? String(c.value) : '';
      const translatedOrOriginal = preferValueAsTranslation && isNumericCell
        ? numericFallback
        : (tr.trim() || numericFallback || this._toWesternNumerals(orig || cellText));
      if (translatedOrOriginal) translatedParts.push(translatedOrOriginal);
    }

    const rawText = rawTextParts.join(' ').trim();
    const text = textParts.join(' ').trim();
    const mergedTranslatedRawText = translatedParts.join(' ').trim();

    const out: any = { text, rawText: rawText || text, raw_text: rawText || text };
    // AUTO-4151: without a columnNumber, sanitizeValueCells treats the merged cell as a value cell
    // on the next grid rebuild and strips `,$'` out of a label. Carry the column identity over.
    const sourceColumnNumber = cellObjects.find(
      c => c && typeof c === 'object' && c.columnNumber != null
    )?.columnNumber;
    if (sourceColumnNumber != null) {
      out.columnNumber = sourceColumnNumber;
    }
    if (mergedTranslatedRawText) {
      out.translatedRawText = mergedTranslatedRawText;
    }
    if (mergedSourceBox) {
      out.sourceBox = mergedSourceBox;
      out.sourceBox.rawText = rawText;
    }
    if (mergedSourceBox != null && mergedSourceBox.pageNumber != null) {
      out.pageNumber = mergedSourceBox.pageNumber;
    } else if (pageNumber !== undefined) {
      out.pageNumber = pageNumber;
    }
    if (allFootnotes.length) {
      out.footnotes = allFootnotes;
    }
    out.isMergeColumn = true;
    return out;
  }

  private unionSourceBox(a: any, b: any): any {
    if (!a || !b || a.pageNumber !== b.pageNumber) {
      return a || b;
    }
    const minX = Math.min(
      a.ulc?.x ?? a.llc?.x ?? 0,
      b.ulc?.x ?? b.llc?.x ?? 0
    );
    const minY = Math.min(
      a.ulc?.y ?? a.llc?.y ?? 0,
      b.ulc?.y ?? b.llc?.y ?? 0
    );
    const maxX = Math.max(
      a.urc?.x ?? a.lrc?.x ?? 0,
      b.urc?.x ?? b.lrc?.x ?? 0
    );
    const maxY = Math.max(
      a.lrc?.y ?? a.urc?.y ?? 0,
      b.lrc?.y ?? b.urc?.y ?? 0
    );
    return {
      pageNumber: a.pageNumber,
      ulc: { x: minX, y: minY },
      urc: { x: maxX, y: minY },
      llc: { x: minX, y: maxY },
      lrc: { x: maxX, y: maxY },
      pageSize: a.pageSize ?? b.pageSize,
    };
  }

  private ensureSourceBoxHasPageSize(box: any, cellObjects: any[]): any {
    if (!box) {
      return null;
    }
    if (box.pageSize?.width > 0 && box.pageSize?.height > 0) {
      return box;
    }
    for (const cell of cellObjects) {
      const ps = cell?.sourceBox?.pageSize;
      if (ps?.width > 0 && ps?.height > 0) {
        return { ...box, pageSize: ps };
      }
    }
    return box;
  }

  private spliceStatementOptionsColumns(startCol: number, numColsToMerge: number): void {
    if (this.statementOptions[this.activePage].length === 0) {
      return;
    }
    for (let i = 0; i < numColsToMerge - 1; i++) {
      this.statementOptions[this.activePage].splice(startCol + 1, 1);
    }
  }

  private spliceMetadataColumns(startCol: number, numColsToMerge: number): void {
    // pageData / cells / statementOptions are kept in lockstep above; model.metadata
    // must follow the same width or _setPageTabularData will rebuild the grid using a
    // stale-wide first row and render too many columns on the next refresh.
    const metadata = this.model.document.pages[this.activePage]?.metadata;
    if (!metadata?.length) {
      return;
    }
    metadata.forEach(row => row.splice(startCol + 1, numColsToMerge - 1));
  }

  // suppressUndo=true: redo path; skips snapshot+push.
  removeColumns(selection, suppressUndo: boolean = false): void {
    const snapshot = suppressUndo ? null : this.captureSnapshot();

    selection.sort((a, b) => (a.start.col > b.start.col) ? -1 : 1 );

    const mergedSelections = [];
    selection.forEach((s, idx: number) => {
      if (idx === 0) {
        mergedSelections.push(s);
      } else if (selectionsOverlap(mergedSelections[mergedSelections.length - 1], s)) {
        mergedSelections[mergedSelections.length - 1] = mergeSelections(mergedSelections[mergedSelections.length - 1], s);
      } else {
        mergedSelections.push(s);
      }
    });

    mergedSelections.forEach(el => {

      const grid = [];
      // If it's the same, we still want to remove 1.
      const columnsToRemove = (el.end.col - el.start.col) + 1;
      this.pageData.forEach(row => {
        row.splice(el.start.col, columnsToRemove);
        grid.push(row.slice(0));
      });
      this.pageData = grid;

      const cells = [];
      this.model.document.pages[this.activePage].cells.forEach(row => {
        row.splice(el.start.col, columnsToRemove);
        cells.push(row.slice(0));
      });
      this.model.document.pages[this.activePage].cells = cells;
    });

    this.reloadTableData();
    // AUTO-2822: matches mergeRows at :1779 — column-splice paths must reconcile the
    // errorStore explicitly. Handsontable's re-render cleans up stale entries on row
    // shrinks but not on column shrinks, leaving entries at keyed coords the render
    // never revisits. Also prune the backend store's out-of-bounds entries.
    this.validatePage(this.activePage);
    this.cellErrorsFromBackendValidation.clearPage(this.activePage);

    if (snapshot) {
      const selectionRef: SelectionRef = { selection: JSON.parse(JSON.stringify(selection)) };
      this.recordUndoableAction(
        snapshot,
        REVIEW_ACTION_TYPES.REMOVE_COLUMNS,
        () => this.removeColumns(selectionRef.selection, true),
        selectionRef,
      );
    }
  }

  private reloadTableData(): void {
    if (this.handsOnTableInstance) {
      this.handsOnTableInstance.updateSettings({ data: this.pageData }, false);
    }
  }

  private captureSnapshot(): ReviewEditorSnapshot {
    // `?? []` fallbacks keep captureSnapshot robust against partial state during
    // tests or transient init; JSON.stringify(undefined) is undefined and JSON.parse
    // of that throws. In production every field is initialized.
    const page = this.model?.document?.pages?.[this.activePage];
    const pageDataClone = JSON.parse(JSON.stringify(this.pageData ?? []));
    // Derive metadata from pageData top rows so col counts stay consistent when insertColumn widened pageData but left model.metadata stale.
    const metadataPage = pageDataClone.slice(0, page?.metadata?.length ?? 0);
    return {
      activePage: this.activePage,
      pageData: pageDataClone,
      cells: JSON.parse(JSON.stringify(page?.cells ?? [])),
      statementOptionsPage: JSON.parse(JSON.stringify(this.statementOptions?.[this.activePage] ?? [])),
      metadataPage,
      selectionBefore: this.handsOnTableInstance?.getSelected() ?? null,
    };
  }

  private applySnapshot(snapshot: ReviewEditorSnapshot): void {
    if (snapshot.activePage !== this.activePage) {
      return;
    }
    // Re-clone on every apply so the snapshot stays pristine across undo→redo→undo
    // cycles. Without this, mutations would corrupt the snapshot via shared refs.
    const restored: ReviewEditorSnapshot = JSON.parse(JSON.stringify(snapshot));
    this.model.document.pages[snapshot.activePage].cells = restored.cells;
    this.statementOptions[snapshot.activePage] = restored.statementOptionsPage;
    this.model.document.pages[snapshot.activePage].metadata = restored.metadataPage;
    // Rebuild from restored cells using CURRENT viewInNativeLanguage so undo/redo never alters the toggle.
    this._setPageTabularData();
    // updateSettings({data}) syncs Handsontable's index mappers to the restored
    // dimensions; render() alone wouldn't (rows/cols that disappeared during the
    // action wouldn't reappear). Source 'updateData' preserves the undo stack —
    // only 'loadData' clears it.
    this.handsOnTableInstance?.updateSettings({ data: this.pageData }, false);
    // selectCells (plural) restores every range so multi-range (Ctrl+click)
    // selections come back intact.
    if (restored.selectionBefore?.length) {
      this.handsOnTableInstance?.selectCells(
        restored.selectionBefore as Array<[number, number, number, number]>,
      );
    }
    // Refresh cell validation so stale error/warning highlights from the undone action clear.
    this.validatePage(this.activePage);
  }

  private isUndoActionAvailable(): boolean {
    const plugin = this.handsOnTableInstance?.getPlugin('undoRedo') as any;
    return !!plugin?.isUndoAvailable?.();
  }

  private isRedoActionAvailable(): boolean {
    const plugin = this.handsOnTableInstance?.getPlugin('undoRedo') as any;
    return !!plugin?.isRedoAvailable?.();
  }

  // @TechDebt: like undo-actions.ts, these touch Handsontable's private plugin fields
  // (doneActions, undoneActions). Re-verify on Handsontable upgrade past 13.x.
  private saveCurrentPageStack(pageIndex: number): void {
    if (!this.handsOnTableInstance || this.handsOnTableInstance.isDestroyed || pageIndex == null) {
      return;
    }
    const plugin = this.handsOnTableInstance.getPlugin('undoRedo') as any;
    if (!plugin) {
      return;
    }
    this.pageHistories.set(pageIndex, {
      doneActions: plugin.doneActions.slice(),
      undoneActions: plugin.undoneActions.slice(),
    });
  }

  private loadPageStack(pageIndex: number): void {
    if (!this.handsOnTableInstance || this.handsOnTableInstance.isDestroyed) {
      return;
    }
    const plugin = this.handsOnTableInstance.getPlugin('undoRedo') as any;
    if (!plugin || !Array.isArray(plugin.doneActions) || !Array.isArray(plugin.undoneActions)) {
      this.logger?.error?.(
        'Handsontable UndoRedo internals (doneActions/undoneActions) not as expected — ' +
        'per-tab undo state may be stale. See undo-actions.ts @TechDebt: note.',
        { hasPlugin: !!plugin },
      );
      return;
    }
    const stored = this.pageHistories.get(pageIndex);
    plugin.doneActions = stored?.doneActions ?? [];
    plugin.undoneActions = stored?.undoneActions ?? [];
  }

  private removePageFromHistories(pageIndex: number): void {
    const updated = new Map<number, { doneActions: any[]; undoneActions: any[] }>();
    this.pageHistories.forEach((value, key) => {
      if (key === pageIndex) {
        return;
      }
      updated.set(key > pageIndex ? key - 1 : key, value);
    });
    this.pageHistories = updated;
  }

  private recordUndoableAction(
    snapshot: ReviewEditorSnapshot,
    actionType: string,
    redoFn: () => void,
    selectionRef?: SelectionRef,
  ): void {
    // Closures avoid attaching `this` to the action — see undo-actions.ts header.
    const undoFn = () => this.applySnapshot(snapshot);
    // Defensive: per-tab stacks already isolate actions to their page, so this
    // should always pass. Kept as insurance against partial state during async ops.
    const guardedRedoFn = () => {
      if (snapshot.activePage === this.activePage) {
        redoFn();
      }
    };
    pushReviewEditorAction(
      this.handsOnTableInstance,
      () => new ReviewEditorAction({
        actionType,
        snapshot,
        undoFn,
        redoFn: guardedRedoFn,
        selectionRef,
      }),
      this.logger,
    );
  }

  // Fail loudly via Sentry if a Handsontable upgrade changes the private undo plugin shape.
  private assertUndoRedoPluginShape(): void {
    const plugin = this.handsOnTableInstance?.getPlugin('undoRedo') as any;
    if (!plugin) {
      this.logger.error('Handsontable undoRedo plugin missing — review undo will not record actions');
      return;
    }
    if (typeof plugin.done !== 'function' || !Array.isArray(plugin.doneActions) || !Array.isArray(plugin.undoneActions)) {
      this.logger.error(
        'Handsontable undoRedo plugin shape changed — review undo will silently no-op until adapted',
        {
          doneType: typeof plugin.done,
          doneActionsType: typeof plugin.doneActions,
          undoneActionsType: typeof plugin.undoneActions,
        },
      );
    }
  }

  // Inserts modify pageData/cells but push no action; patch existing snapshots so a later undo doesn't wipe them.
  private patchActiveStackForRowInsert(rowIndex: number, numMetadataRows: number): void {
    walkActivePageActions(
      this.handsOnTableInstance?.getPlugin('undoRedo') as any,
      this.activePage,
      (action) => {
        patchSnapshotForRowInsert(
          action.snapshot,
          rowIndex,
          numMetadataRows,
          (numCol) => this._buildNewCellRow(numCol),
        );
        shiftSelectionForRowInsert(action.selectionRef, rowIndex);
      },
    );
  }

  private patchActiveStackForColumnInsert(colIndex: number): void {
    walkActivePageActions(
      this.handsOnTableInstance?.getPlugin('undoRedo') as any,
      this.activePage,
      (action) => {
        patchSnapshotForColumnInsert(
          action.snapshot,
          colIndex,
          () => ({
            text: '',
            raw_text: '',
            translated_raw_text: '',
            sourceBox: { rawText: '' },
            isNewcell: true,
          }),
          () => JSON.parse(JSON.stringify(this._environmentService.getStatementOptions())),
        );
        shiftSelectionForColumnInsert(action.selectionRef, colIndex);
      },
    );
  }

  private patchActiveStackForInsertFrom(
    rowIndex: number,
    columnsWidenedBy: number,
    numMetadataRows: number,
    insertedCellRows: any[][],
    insertedPageDataRows: any[][],
  ): void {
    walkActivePageActions(
      this.handsOnTableInstance?.getPlugin('undoRedo') as any,
      this.activePage,
      (action) => {
        patchSnapshotForInsertFrom(
          action.snapshot,
          rowIndex,
          columnsWidenedBy,
          numMetadataRows,
          insertedCellRows,
          insertedPageDataRows,
          () => ({
            text: '',
            raw_text: '',
            translated_raw_text: '',
            sourceBox: { rawText: '' },
            isNewcell: true,
          }),
          () => JSON.parse(JSON.stringify(this._environmentService.getStatementOptions())),
        );
        shiftSelectionForRowInsertBy(action.selectionRef, rowIndex, insertedPageDataRows.length);
      },
    );
  }

  setOptions(hotTableInstance: Handsontable): void {
    let self = this;
    hotTableInstance.updateSettings({
      afterOnCellMouseDown: (e, coords) => this.handleCellClick(e, coords),
      cells: function(row, col) {
        const cellProperties = {};

        // A new page that is created does not populate with statement options so we cannot rely on the object
        if (self.statementOptions[self.activePage].length === 0) {
          // We have a new page which contains nothing
          return cellProperties
        }

        if (row < self.statementOptions[self.activePage][col]?.length && col === 0) {
          cellProperties['readonly'] = true;
        }
        if (row < self.statementOptions[self.activePage][col]?.length && col > 0) {
          cellProperties['type'] = self.statementOptions[self.activePage][col][row].type;
          if (cellProperties['type'] === 'dropdown') {
            cellProperties['source'] = self.statementOptions[self.activePage][col][row].values;
          }
          // don't allow the type to override the custom renderer
          this.renderer = self.renderCell;
        }

        if (!self.viewInNativeLanguage && col === 0 &&
            row >= (self.statementOptions[self.activePage][col]?.length || 0) &&
            self._cellHasTranslation(row, col)) {
          cellProperties['readOnly'] = true;
          cellProperties['editor'] = false;
        }

        return cellProperties;
      },
      viewportRowRenderingOffset: REVIEW_TABLE_ROW_RENDER_BUFFER,
      viewportColumnRenderingOffset: 1,
    }, false);
  }

  ngOnDestroy() {
    if (this._activeDataReviewGuide) {
      this.userGuideService.remove(this._activeDataReviewGuide);
    }
    this.userGuideService.remove(USER_GUIDES.REMOVING_DATA);
    this.userGuideService.remove(USER_GUIDES.SUPPORTED_TAX_RETURNS);

    this.pageHistories.clear();
    this.setTopAndSideNavDisplay(true);
  }

  toggleStepsToSuccess() {
    if (this._sharedDataService.shouldShowManualReviewSidebar === true) {
      this._sharedDataService.shouldShowManualReviewSidebar = false;
    } else {
      this._sharedDataService.shouldShowManualReviewSidebar = true;
    }
  }

  setTopAndSideNavDisplay(value: boolean) {
    this._sharedDataService.shouldShowTopNav = value;
    this._sharedDataService.shouldShowSidebar = value;
  }

  retrieveReviewQueueItem(): void {
    firstValueFrom(this._apiService.send('Post', `/api/review-queue-items/${this.reviewQueueItemId}/retrieve-for-review`)).then(data => {
      this.isLoading = false;
      this.model = data.response.objects[0];
      if (this.model && this.model.embeddedWorkflow) {
        this._sharedDataService.embeddedWorkflow$.next(this.model.embeddedWorkflow);
      }

      this.model.document.pages.map(page => {
        this.addMissingMetadataToPage(page);
        this.sortPageMetadata(page);
        ReviewEditorComponent.setMetadataScenarioTypeUsingDate(page, this.model);
      });

      this.setupStatementOptions();

      // Deconstruct and make a deep copy of object so when we pass to child component it's a reference to the copy not the original
      this.modelCopy = { ...this.model };
      this.modelCopy = Object.assign({}, this.model);
      this.modelCopy = JSON.parse(JSON.stringify(this.model));

      const docIdFromJobPayload = this.model?.jobPayload?.documentFile?.id;

      (this.model as any).documentFileId = docIdFromJobPayload;
      (this.modelCopy as any).documentFileId = docIdFromJobPayload;

      try {
        this._trackingService.trackHumanInLoop({
          type: 'Start',
          step: 'Manual Review',
          documentFileId: this.model.document.fileKey.split('/')[0],
          documentCompanyId: this.model.jobPayload.company.id,
          documentTenantId: this.model.jobPayload.context.bankId
        });
      } catch (err) {
        console.log('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
        this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
      }

      if (this.model.jobPayload.documentFile.type == TAX_RETURN) {
        this.isRoundingDropdownDisabled = true;
        this.roundingSelection = null;
      }
      this.roundingSelection = this.model.multiplyByFactor;

      this.currency = this.model.currency;

      this.setDefaultReportingInterval();
      this.showPage(0);
      this._restoreTranslationView();
      this.addOrRemoveShowSourceFromContextMenu();
      this.addInsertIntoTableOptions();
      this.isLoading = false;
    }).catch(err => {
      this.isLoading = false;
      this.modelLoadError = err.message;
      this.isError = true;
      // adding some console logs so we can also see error in FullStory recordings
      console.log('Error in retrieveReviewQueueItem');
      console.log(err.message);
      console.log(err);
      this.logger.error('Error in retrieveReviewQueueItem: ' + err.message, {'errorObject': err});
    });

    this.subsArr$.push(this._reviewQueueService.retrieveAndLock(this.reviewQueueItemId).subscribe((item: ReviewQueueItem) => {
      this.reviewQueueItem = item;
      if(this.reviewQueueItem.status == DOC_PROCESSING_STATUS) {
        this.forceReloadService.refreshBrowser() // something went wrong, force reload the page
        return;
      }

    }, err => {
      this._alertService.error(err);
    }));

    return;
  }

  isReportingIntervalShown(): boolean {
    if (this.model.document.pages[this.activePage].reportingInterval === 'None' ||
        this.model.document.pages[this.activePage].reportingInterval === '' ||
        this.model.document.pages[this.activePage].reportingInterval === undefined) {
      return false;
    }
    return true;
  }

  isAdditionalInformationShown(): boolean {
    if (this.model.jobPayload.documentFile.additionalInformation !== '') {
        return true;
    }
    return false;
  }

  updateRoundingDropdownValidity(): void {
    this.isRoundingDropdownInvalid = false;
  }

  /**
   * Determines the reporting interval by parsing the incoming reported_date from
   */
  setDefaultReportingInterval() {
    const reportedDate = this.model.jobPayload.documentFile.reportedDate;

    if (!reportedDate) {
      this.reportingInterval = 'Unknown';
    } else {
      this.reportingInterval = CommonFunctions.getPeriodTypeAsString(this.model.jobPayload.documentFile.reportedDate);
      this.setReportingIntervalAllPagesIfNull(this.reportingInterval);
    }
  }


  showScreen(screen: string): void {
    this.currentScreen = screen;
  }

  _formatDate(text) {
    const date = moment(text);
    if (date.isValid()) {
      return date.endOf('month').format('MM/DD/YYYY');
    }
    return '#ERR Invalid Date: (' + text + ')';
  }

  /**
   * Function that formats the value of a cell based on the given currency type
   *
   * Formats:
   * USD , CAD, EUR, AUD, HKD, MXN, GBP, SGD: 100,000.00
   * JPY, KRW: 100,000
   * INR: 1,00,00.00
   * RUB: 100.00,00
   * CHF: 100'000.00
   *
   * @param str
   */
  _formatNumber(str: string) {
    return this._currencyFormattingService.formatCurrency(str, this.model.currency);
  }

  changeCurrency(currency: string) {
    this.model.currency = currency;
    this.showPage(this.activePage);
  }

  /**
  * Function to set statement options.
  * rowNumber: The index in the statement options to pass to setDataAtCell in order to set the correct cell.
  * value: Value to set the content of the row at rowNumber to.
  **/
  autoSetStatementOptions(rowNumber: number, value?: string) {
    let idxOfOption;

    // If statment options don't exist for the page, there is no metadata to autoset
    if (this.statementOptions[this.activePage].length === 0) {
      return;
    }

    if (this.model.jobPayload.documentFile.type === TAX_RETURN && !value) {
      console.log('is tax return');
      if (rowNumber === MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX) {
        console.log('is MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX which is', MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX);
        // Since this is a tax return, we know that the reporting interval is a year so set the reporting interval to
        // yearly.
        // Use column one here as column one contains the default statement options.
        const columnNumber = 1;
        idxOfOption = this.statementOptions[this.activePage][columnNumber][rowNumber]
          .values.findIndex(option => option === REPORTING_INTERVAL_ANNUAlLY_KEY);
      } else if (rowNumber === MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX) {
        console.log('is MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX which is', MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX);
        // Since this is a tax return, we know that the preparation type is a tax return so set the preparation type
        // to tax return.
        // Use column zero here as column zero contains the default statement options.
        const columnNumber = 0;
        idxOfOption = this.statementOptions[this.activePage][columnNumber][rowNumber]
          .values.findIndex(option => option === PREPARATION_TYPE_TAX_RETURN_KEY);
      }
    } else if (value) {
      console.error('value: ' + value);
      // Use column one here as column one contains the default statement options.
      const columnNumber = 1;
      idxOfOption = this.statementOptions[this.activePage][columnNumber][rowNumber]
        .values.findIndex(option => option === value);
    }
    if (this.pageData.length >= 1 && this.handsOnTableInstance && idxOfOption >= 0) {
      let col = 0;
      this.pageData[rowNumber].forEach(() => {
        if (col > 0) {
          this.handsOnTableInstance.setDataAtCell(
            rowNumber,
            col,
            this.statementOptions[this.activePage][col][rowNumber].values[idxOfOption]
          );
        }
        col++;
      });
    }
  }


  /**
   * Function passed to hands on table to render the TD for each individual
   * cell. We use this to apply styling rules based on validation.
   */
  _renderCell(instance, td, row, col, prop, cell: string, cellProperties) {
    // Handle case when new row is added
    if (cell === null) {
      cell = '';
    }

    this._renderCellContent(instance, td, row, col, prop, cell, cellProperties)

    const currentBackendValidationStatus = this.cellErrorsFromBackendValidation.getCellValue(this.activePage, row, col);
    if (currentBackendValidationStatus !== undefined) {
      this._applyCellStylingForBackendValidationErrors(currentBackendValidationStatus, row, col, cell, td);
    }
    if (col === 0) {
      td.className = td.className + ' fs-exclude fs-block';
      if (!this.viewInNativeLanguage &&
          row >= (this.statementOptions[this.activePage]?.[col]?.length || 0) &&
          this._cellHasTranslation(row, col)) {
        td.classList.add('translated-label-cell');
      }
    }
    // Clear any lingering cell validation error message
    this.cellValidationMessage = '';
    this.cellValidationHeading = '';

    const page = this.model.document.pages[this.activePage];

    const validationFailure = getCellValidationFailure(this.activePage, row, col, cell, this.statementOptions,
      this._currencyFormattingService.getDecimalSeparator(this.model.currency), this.model.document.type,
      this.numDecimalsMode, this.handsOnTableInstance);
    td.__validationFailure = validationFailure;
    let flagText = false;
    if (
      this.model.jobPayload.documentFile.type === TAX_RETURN &&
      this.statementOptions[this.activePage].length > 0 &&
      row >= this.statementOptions[this.activePage][col].length
    ) {
      if (
        page.cells[row - this.statementOptions[this.activePage][col].length] &&
        page.cells[row - this.statementOptions[this.activePage][col].length][col] &&
        page.cells[row - this.statementOptions[this.activePage][col].length][col].hasOwnProperty('flagText') &&
        page.cells[row - this.statementOptions[this.activePage][col].length][col]['flagText']
      ) {
        Object.assign(td.style, WARNING_CELL_STYLE); // if warning, color yellow
        this.cellErrors.setCellStatus(this.activePage, row, col, WARNING_STATUS);
        td.__validationFailure = taxReturnCellFlagCopy;
        flagText = true;
      } else {
        this.cellErrors.clearCellStatus(this.activePage, row, col);
      }
    }

    if (!!validationFailure || flagText) {
      if (!!validationFailure) {
        if (isWarningCell(cell, row, col, validationFailure)) {
          Object.assign(td.style,WARNING_CELL_STYLE)
          this.cellErrors.setCellStatus(this.activePage, row, col, WARNING_STATUS);
        } else {
          Object.assign(td.style, PROBLEM_CELL_STYLE); // if error, color red
          this.cellErrors.setCellStatus(this.activePage, row, col, ERROR_STATUS);
        }
      }

      if (!td.__boundEvents) {
        td.addEventListener('mouseenter', () => {
          td.__hovered = true;
          if (td.__validationFailure) {
            setTimeout(() => {
              if (td.__hovered) {
                this.showCellErrorTooltip(td);
              }
            }, ValidationTooltipDelayTime)
          }
        });
        td.addEventListener('mouseleave', () => {
          td.__hovered = false;
          if (td.__validationFailure) {
            this.hideCellErrorTooltip(td);
          }
        });

        td.__boundEvents = true;
      }
    } else {
      this.cellErrors.clearCellStatus(this.activePage, row, col);
      if (ReviewEditorComponent.cellIsAHeader(row, col, this.statementOptions, this.activePage)) {
        Object.assign(td.style, OPTION_LABEL_CELL_STYLE);
        this.cellErrors.clearCellStatus(this.activePage, row, col);
      }
    }
    return td;
  }

  _renderCellContent(instance, td, row, col, prop, cell, cellProperties){
    if (cellProperties?.type == 'dropdown') {
      const dropdownRenderer = Handsontable.renderers.getRenderer('dropdown')
      dropdownRenderer(instance, td, row, col, prop, cell, cellProperties);
    } else if (cellProperties?.type == 'text') {
      const textRenderer = Handsontable.renderers.getRenderer('text');
      textRenderer(instance, td, row, col, prop, cell, cellProperties);
    } else if (isValidValueCell(cell) && cell !== '' && cell.search(notNumberWithDecimalCommaDashRegex) === -1) {
      td.innerHTML = this._formatNumber(cell);
      td.align = 'right';
    } else {
      td.innerHTML = cell;
      td.align = 'left';
    }

    if (col === 0 && instance) {
      if (this._rowHasNoAmounts(instance, row)) {
        td.style.fontWeight = 'bold';
      } else {
        td.style.fontWeight = 'normal';
      }
    }
  }

  _rowHasNoAmounts(instance, row): boolean {
    if (!instance) {
      return false;
    }
    const rowData = instance.getSourceDataAtRow(row);
    if (!rowData || !Array.isArray(rowData)) {
      return false;
    }
 
    for (let col = 1; col < rowData.length; col++) {      
      const cellValue = rowData[col];
      if (cellValue !== null && cellValue !== undefined && cellValue !== '') {
        return false;
      }
    }
    return true;
  }

  _applyCellStylingForBackendValidationErrors(currentBackendValidationStatus, row, col, cell, td){
      /**
       * Cell errors originating from server-side validation need to be handled separately b/c the frontend is not aware
       * of the backend validation rules. The frontend is made aware of a backend validation failure when a submit
       * request receives an error response. We flag the problematic cell in the error manager dict by assigning a known
       * static value. On the next render of the error cell, we replace the static flag value with the initial value of
       * the cell. On all subsequent renders, we compare the current value to the initial value, and clear the error
       * state if the user has made a change to the cell. This allows the UX to reflect a user 'resolving' an  error,
       * since we are unable to reevaluate the initial validation failure on the frontend.
       */
      if (currentBackendValidationStatus === INITIAL_INVALID_VALUE){
        // initial render after receiving error response
        this.cellErrorsFromBackendValidation.setCellStatus(this.activePage, row, col, cell);
        Object.assign(td.style, PROBLEM_CELL_STYLE); // if error, color red
      } else if (currentBackendValidationStatus === cell){
        // subsequent render, cell value has not been edited
        Object.assign(td.style, PROBLEM_CELL_STYLE); // if error, color red
      } else {
        // Cell value has been changed from it's initial invalid value
        this.cellErrorsFromBackendValidation.clearCellStatus(this.activePage, row, col)
      }
  }

  /**
   * Shows the error tooltip for the cell
   */
  showCellErrorTooltip(cellTd) {
    const rect = cellTd.getBoundingClientRect();
    const failure = cellTd.__validationFailure || '';
    if (failure === LabelExceedsCharLimitCopy) {
      this.cellValidationHeading = LabelExceedsCharLimitHeading;
      this.cellValidationMessage = LabelExceedsCharLimitDescription;
    } else {
      this.cellValidationHeading = '';
      this.cellValidationMessage = failure;
    }
    const middle = rect.left + (rect.right - rect.left) / 2;
    this.cellValidationStyle = {
      'top': (rect.bottom + 5).toString() + 'px',
      'left': middle.toString() + 'px',
    }
    // Need to manually detect changes for re-rendering since it's a mouseover and not a click or some other event that angular automatically re-renders for
    this._changeDetector.detectChanges();
  }

  hideCellErrorTooltip(cellTd) {
    this.cellValidationMessage = '';
    this.cellValidationHeading = '';
    this._changeDetector.detectChanges();
  }

  /**
   * Goes through each page and sets the reporting interval if it is not set
   */
  setReportingIntervalAllPagesIfNull(reportingInterval) {
    this.model.document.pages.forEach(page => {
      // There's a strange backend case in which the value is passed through as "None" - could be dev only but doesn't hurt to cover that here.
      if (!page.reportingInterval || page.reportingInterval === 'None') {
        page.reportingInterval = reportingInterval;
      }
    });
  }


  _setPageTabularData() {
    const page = this.model.document.pages[this.activePage];
    this.sanitizeValueCells(page);
    this.syncValueToTextFields(page);
    // this.pageData = page.cells;
    const grid = []

    // reset decimal counts each time we rebuild the page data so that
    // the mode is calculated only from the currently active page
    this.decimalCounts = [];

    if (page.hasOwnProperty('metadata')) {
      page.metadata.forEach(row => {
        const cleanRow = row.map((string) => {
          return string;
        });
        grid.push(cleanRow);
      });
    }

    page.cells.forEach(row => {
      row.forEach( (col, colIndex) => {
        if (col.rawText === 'None') {
          col.rawText = '';
        }
        if (col.text === 'None') {
          col.text = '';
        }
        if (col.translatedRawText === 'None') {
          col.translatedRawText = '';
        }
        if (col.translated_raw_text === 'None') {
          col.translated_raw_text = '';
        }

        if (colIndex !== 0 && col.text !== '') {
          this.decimalCounts.push(CommonFunctions.numDecimals(col.text));
        }

      });
      const cleanRow = row.map((cell, colIndex) => {
        const translatedText = cell.translatedRawText || cell.translated_raw_text || '';
        if (translatedText && translatedText !== cell.text) {
          this.translationDict[cell.text] = translatedText;
        }
        if (colIndex === 0) {
          if (this.viewInNativeLanguage) {
            return cell.text;
          } else {
            return translatedText || this.translationDict[cell.text] || cell.text;
          }
        }
        if (cell.value != null) {
          return String(cell.value);
        }
        return translatedText || cell.text;
      });

      grid.push(cleanRow);
    });

    this.numDecimalsMode = CommonFunctions.mostFrequentElement(this.decimalCounts);

    this._generateColumns(grid[0].length);
    this.pageData = grid;
  }

  _generateColumns(numberOfColumns: number) {
    const columns = [{
      title: 'Line Items',
    }];

    for (let i = 1; i < numberOfColumns; i++) {
      columns.push({
        title: `Period ${i}`,
      });
    }

    this.columns = columns;
  }

  hasTranslations(): boolean {
    return Object.entries(this.translationDict).some(([key, value]) => key.trim() !== (value as string).trim());
  }

  private _cellHasTranslation(row: number, col: number): boolean {
    const numMetadataRows = this.statementOptions[this.activePage]?.[col]?.length || 0;
    if (row < numMetadataRows) return false;
    const dataRowIndex = row - numMetadataRows;
    const modelCell = this.model.document.pages[this.activePage]?.cells?.[dataRowIndex]?.[col];
    const originalText = modelCell?.text || (typeof modelCell === 'string' ? modelCell : '');
    if (this.translationDict[originalText]) {
      return true;
    }
    // Merged cells lose their dict key; fall back to the stored translation.
    const translated = (modelCell?.translatedRawText || modelCell?.translated_raw_text || '').trim();
    return translated !== '' && translated !== originalText.trim();
  }

  private _selectionIncludesTranslatedLabelColumn(): boolean {
    const selected = this.handsOnTableInstance?.getSelected();
    if (!selected) return false;
    for (let i = 0; i < selected.length; i++) {
      const startCol = Math.min(selected[i][1], selected[i][3]);
      if (startCol === 0) {
        const startRow = Math.min(selected[i][0], selected[i][2]);
        const endRow = Math.max(selected[i][0], selected[i][2]);
        for (let row = startRow; row <= endRow; row++) {
          if (this._cellHasTranslation(row, 0)) return true;
        }
      }
    }
    return false;
  }

  swapLanguage() {
    this._saveCurrentPage();
    this.viewInNativeLanguage = !this.viewInNativeLanguage;
    this._translationViewState.set(this._documentFileId(), this.viewInNativeLanguage);
    this._setPageTabularData();
    if (this.handsOnTableInstance) {
      this.setOptions(this.handsOnTableInstance);
      this.handsOnTableInstance.render();
    }
  }

  private _documentFileId(): number | string | null {
    return (this.model as any)?.documentFileId ?? this.model?.jobPayload?.documentFile?.id ?? null;
  }

  private _restoreTranslationView(): void {
    if (!this.hasTranslations()) {
      return;
    }
    const remembered = this._translationViewState.get(this._documentFileId());
    if (remembered === this.viewInNativeLanguage) {
      return;
    }
    this.viewInNativeLanguage = remembered;
    this._setPageTabularData();
    if (this.handsOnTableInstance) {
      this.setOptions(this.handsOnTableInstance);
      this.handsOnTableInstance.render();
    }
  }
  private _onAfterChange(cellChange: Array<[number, number, string, string]>): void {

    this.autofillMetaDataValues(cellChange);
    this._ensureModelCellsForChangedRows(cellChange);
    this._updateModelCellOnChange(cellChange);
    this.setOptions(this.handsOnTableInstance);
    this.handsOnTableInstance.render();
    this.subsArr$.push(
      this._translateEditedLabels(cellChange).subscribe(() => {
        this._updateModelCellOnChange(cellChange);
        this.setOptions(this.handsOnTableInstance);
        this.handsOnTableInstance.render();
      })
    );
  }

  private _ensureModelCellsForChangedRows(cellChange: Array<[number, number, string, string]>): void {
    if (!cellChange?.length) { return; }
    const page = this.model.document.pages[this.activePage];
    const cells = page?.cells;
    if (!cells?.length) { return; }

    const metadataLength = page.metadata?.length || 0;
    const numCol = cells[0]?.length || 0;
    if (!numCol) { return; }

    const dataRowIndices = cellChange
      .filter(change => change && change[0] >= metadataLength)
      .map(([gridRow]) => gridRow - metadataLength);
    if (!dataRowIndices.length) { return; }

    const maxDataRowIndex = Math.max(...dataRowIndices);
    while (cells.length <= maxDataRowIndex) {
      const newRow = [];
      for (let i = 0; i < numCol; i++) {
        newRow.push({ text: '', raw_text: '', translated_raw_text: '', isNewcell: true });
      }
      cells.push(newRow);
    }
  }

  private _translateEditedLabels(cellChange: Array<[number, number, string, string]>): Observable<void> {
    if (!cellChange || !this.hasTranslations()) { return of(undefined); }

    const labelsToTranslate = cellChange
      .filter(change => change && change[3] !== change[2] && !!change[3])
      .map(([rowIndex, columnIndex, , newVal]) => ({ rowIndex, columnIndex, newVal: newVal.trim() }))
      .filter(({ newVal }) =>
        this.translationDict[newVal] === undefined && !(newVal !== '' && !isNaN(Number(newVal)))
      );

    if (!labelsToTranslate.length) { return of(undefined); }

    const translations$ = labelsToTranslate.map(({ rowIndex, columnIndex, newVal }) =>
      this._translationService.translateLabel(newVal).pipe(
        tap((translated) => {
          if (!translated || translated === newVal) {
            return;
          }
          this.translationDict[newVal] = translated;
          if (!this.viewInNativeLanguage && this.pageData?.[rowIndex] != null) {
            this.pageData[rowIndex][0] = translated;
            this.handsOnTableInstance?.render();
          } else if (columnIndex !== 0) {
            this.pageData[rowIndex][columnIndex] = translated;
            this.handsOnTableInstance?.render();
          }
        }),
        catchError((err) => {
          this.logger.error('Failed to translate edited label', { errorObject: err });
          return of(null);
        })
      )
    );

    return forkJoin(translations$).pipe(map(() => undefined));
  }

  save(complete = false, continueToSpreading = false) {
    if (!this._validateBeforeSave()) {
      return;
    }

    this._executeSave(complete, continueToSpreading);
  }

  private _executeSave(complete: boolean, continueToSpreading: boolean) {
    this._changeDetector.detectChanges();

    this.model.document.pages.map(page => {
      page.metadata.map(metadata => {
        if (metadata[0] === SCENARIO_TYPE) {
          metadata.map(md => {
            if (md === SCENARIO_TYPE_PROJECTION) {
              console.error('PROJECTION found');
            }
          });
      }});
    });

    this._saveCurrentPage();
    if (complete) {
      this.model.status = 'complete';
      this.closeSplitscreen();
    } else {
      this.isLoading = true;
    }

    this.cleanDocumentOfNones();
    firstValueFrom(this._apiService.send('Patch', `/api/review-queue-items/${this.reviewQueueItemId}`, {
      parsed_document: this.model,
      multiply_by_factor: this.roundingSelection,
    })).then(data => {
      if (!complete) {
        this.isLoading = false;
      }
      if (this.handsOnTableInstance && !this.handsOnTableInstance.isDestroyed) {
        this.handsOnTableInstance.clearUndo();
      }
      this.pageHistories.clear();

      if (complete) {
        if (!continueToSpreading || !this.statusModalPopup) {
          this._routeToNextPage(data, continueToSpreading);
        }
        return;
      } else {
        this._alertService.success('File saved!');
      }
    }).catch(error => {
      this.isLoading = false;
      this.isCompletingReview = false;

      if (this.statusModalPopup) {
        this.statusModalPopup.closable = true;
        this._popupService.close(this.statusModalPopup);
        this.statusModalPopup = null;
      }
      
      // This will still allow raw error messages from certain django validation error to bubble up into toast messages,
      // instead of the generic 'unknown error' message. Leaving this functionality for now, since we don't have targeted
      // error handling for every possible validation failure, and the raw django message is likely to provide some
      // guidance to a user. In the future, if we have better handling of all possible validation errors, we can update
      // this logic to pull error message content into the toast message ONLY if an 'error_title' is included in the
      // error response - (we define an error_title for all known validation errors that are directly thrown in code).
      if (error && error?.message && String(error.message) !== 'undefined') {
          const title = error?.error_title ? error.error_title : UNKNOWN_ERROR_TOAST_TITLE;
          this._alertService.error(error.message, title)
      } else {
        this._alertService.error(UNKNOWN_ERROR_TOAST_BODY, UNKNOWN_ERROR_TOAST_TITLE);
      }
      if (error?.cell_validation_error_details) {
        const errorDetails = error.cell_validation_error_details;
        if (errorDetails.hasOwnProperty('page_idx') && errorDetails.hasOwnProperty('col_idx') &&
          errorDetails.hasOwnProperty('row_idx')) {
          this.highlightBackendErrorCell(errorDetails.page_idx, errorDetails.col_idx, errorDetails.row_idx);
        } else if (error.message.includes('Multiple pages are tagged as') && error.error_type === 'ValidationError' &&
          errorDetails.hasOwnProperty('duplicate_statement_type')) {
          this.highlightDuplicateStatementTypeSelectors(errorDetails.duplicate_statement_type)
        }
      }
      // need to re-open the document flyout since it was dismissed by the 'save' action
      this._showDocumentFlyoutIfEnabled();
    });
  };

  highlightBackendErrorCell(page, col, row){
    if ([page, col, row].some(val => !Number.isInteger(val))){
      // confirm that all necessary cell coordinates were provided in the api response
      return
    }
    this.cellErrorsFromBackendValidation.setCellStatus(page, row, col, INITIAL_INVALID_VALUE)
    this.assignPageValidationStatus(page);
  }

  highlightDuplicateStatementTypeSelectors(statementType){
    this.duplicateSelectedStatementType = statementType
  }

  _routeToNextPage(data: any, continueToSpreading = false) {
    const companyId: number = data && data.company_id || null;
    const statementType: string = data && data.processing_job_payload &&
      data.processing_job_payload.document_file &&
      data.processing_job_payload.document_file.type || null;

    if (continueToSpreading && companyId && statementType) {
      // Important - Spreading happens in the bank context so need to set bank id for admins
      this._userService.setBankIdContext(data.bank_id);

      const docFileUuid = data.processing_job_payload.document_file.uuid;
      try {
        this._trackingService.trackHumanInLoop({
          type: 'End',
          step: 'Manual Review',
          documentFileId: this.model.document.fileKey.split('/')[0],
          documentCompanyId: this.model.jobPayload.company.id,
          documentTenantId: this.model.jobPayload.context.bankId
        });
      } catch (err) {
        console.log('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
        this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
      }

      this._router.navigate(['spread', docFileUuid]);
    } else {
      this._router.navigate(['review']);
    }
  }

  showStatusPopup(): Promise<NgxPopupComponent> {
    try {
      this._trackingService.trackHumanInLoop({
        type: 'End',
        step: 'Manual Review',
        documentFileId: this.model.document.fileKey.split('/')[0],
        documentCompanyId: this.model.jobPayload.company.id,
        documentTenantId: this.model.jobPayload.context.bankId
      });
    } catch (err) {
      this.logger.error('Error in tracking human-in-loop event: ' + err.message, {'errorObject': err});
    }

    if (this.model?.jobPayload?.context?.bankId) {
      this._userService.setBankIdContext(this.model.jobPayload.context.bankId);
    }

    return this._popupService.open({
      componentType: StatusModalComponent,
      cssClass: 'modal-pipeline-processing',
      inputs: {
        reviewQueueItemId: this.reviewQueueItemId,
        fromStep: FINAL_REVIEW_STEP,
      },
    }).then((popup: NgxPopupComponent) => {
      this.statusModalPopup = popup;
      popup.addEventListener('close', () => {
        this.isLoading = false;
        this.isCompletingReview = false;
        this.statusModalPopup = null;
      }, { once: true });
      return popup;
    });
  }

  _saveCurrentPage() {
    if (this.pageData == null || this.activePage == null) {
      return;
    }

      this.pageData.forEach(row => {
      for (let col = 0; col < row.length; col++) {
        if (row[col] == null || row[col] === undefined) {
          row[col] = '';
        }
      }
    });

    const numMetadataRows = this.statementOptions[this.activePage]?.[0]?.length || 0;
    const page = this.model.document.pages[this.activePage];

    const metadata = [];
    for (let i = 0; i < numMetadataRows && i < this.pageData.length; i++) {
      metadata.push(this.pageData[i].map(cell => cell));
    }
    page.metadata = metadata;

    page.cells.forEach(row => {
      const modelCell = row[0];
      if (modelCell?.text && this.translationDict[modelCell.text]) {
        modelCell.translatedRawText = this.translationDict[modelCell.text];
      }
    });

    this.updateCurrencyForDocumentFile();
  }

  updateCurrencyForDocumentFile() {
    const body = {
      currency: this.model.currency
    };
    this.subsArr$.push(this._documentFileService.updateDocumentFile(this.model.jobPayload.documentFile.id, body).subscribe(response => {
      console.log(response);
    }));
  }

  addPage() {
    const newPage = {
      'statement_type': null,
      'cells': [[{'text': '', 'raw_text': ''}]],
      'workPredictors': {},
      'metadata': []
    };
    this.model.document.pages.push(newPage);
    this.statementOptions.push([]);

    this.statementOptions[this.statementOptions.length - 1][0] = JSON.parse(JSON.stringify(this._environmentService.getStatementOptions()));

    this.statementOptions[this.statementOptions.length - 1][0].forEach(option => {
        newPage.metadata.push([option.label]);
    });

    this.showPage(this.model.document.pages.length - 1, true);
    this.insertColumn([{ start: { col: 1, row: 0 }, end: { col: 1, row: this.statementOptions[this.statementOptions.length - 1][0].length + 1}}], false);

    this.addInsertIntoTableOptions();
  }

  isDocumentLocked(): boolean {
    return this._reviewQueueService.isDocumentLocked(this.model);
  }

  deletePage(pageNumber: number, overrideConfirmation = false): void {
    if (overrideConfirmation || confirm('Are you sure you want to delete this page?')) {
      this._saveCurrentPage();
      this.model.document.pages.splice(pageNumber, 1);
      this.statementOptions.splice(pageNumber, 1)
      this.removePageFromHistories(pageNumber);
      // AUTO-2822: keep page-indexed maps aligned with the newly-spliced pages array so
      // entries at index > pageNumber don't silently apply to the page that now takes
      // that index. cellErrors is reset wholesale by validateAllPages() below; the
      // backend store and pageStatuses need explicit compaction.
      this._compactMapByPageIndex(this.cellErrorsFromBackendValidation.errorStore as unknown as Record<number, unknown>, pageNumber);
      this._compactMapByPageIndex(this.pageStatuses as unknown as Record<number, unknown>, pageNumber);

      // Pages to the left of activePage shifted left by 1 after the splice; keep
      // activePage pointing at the same logical page so the user stays on the tab
      // they were on (e.g. after Insert From's removeInsertedPage).
      if (pageNumber < this.activePage) {
        this.activePage -= 1;
      }

      if (this.model.document.pages.length === 0) {
        this.addPage();
      } else if (this.model.document.pages.length === this.activePage) {
        this.showPage(this.model.document.pages.length - 1, false);
      } else if (this.model.document.pages.length > 0) {
        this.showPage(this.activePage, false);
      } else {
        this.activePage = null;
      }
      this.duplicateSelectedStatementType = null;
      this.validateAllPages();
    }
    this.addInsertIntoTableOptions();
  }

  showPage(pagenum, save = true) {
    if (save) {
      this._saveCurrentPage();
    }
    // Only swap plugin stacks on an actual page switch. Otherwise a same-page
    // refresh (e.g. deletePage rescheduling showPage on the current tab) would
    // overwrite the live plugin stack with an empty pageHistories entry.
    const isSwitching = this.activePage != null && this.activePage !== pagenum;
    if (isSwitching) {
      this.saveCurrentPageStack(this.activePage);
    }

    this.activePage = pagenum;
    this._setPageTabularData();
    if (isSwitching) {
      this.loadPageStack(pagenum);
    }
    this.autoSetStatementOptions(MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX);
    this.autoSetStatementOptions(MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX);
    this.autoSetStatementOptions(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX);
    this.autoSetStatementOptions(MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX);
    this.autoSetStatementOptions(MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX);

    this._resetTableScroll();
  }

  _resetTableScroll(): void {
    setTimeout(() => {
      if (this.mainColumn) {
        this.mainColumn.nativeElement.scrollTop = 0;
      }
      if (this.handsOnTableInstance && !this.handsOnTableInstance.isDestroyed) {
        this.handsOnTableInstance.scrollViewportTo(0, 0);
      }
    }, 0);
  }

  toggleSplitScreen(){
    if (this.splitscreenOpen){
      this.closeSplitscreen();
    } else {
      this.openSplitscreen();
    }
  }

  openSplitscreen() {
    this.splitscreenOpen = true;
    this.rerenderExcelTable();
  }

  closeSplitscreen() {
    this.viewSource = false;
    this.splitscreenOpen = false;
    this.rerenderExcelTable();
  }

  rerenderExcelTable(): void {
    if (this.handsOnTableInstance) {
      setTimeout(() => {
        if (this.handsOnTableInstance && !this.handsOnTableInstance.isDestroyed) {
          this.handsOnTableInstance.render();
        }
      }, 0);
    }
  }

  addOrRemoveShowSourceFromContextMenu() {
    if (SUPPORTED_VIEW_SOURCE_FILE_TYPES.includes(this.model.document.type)) {
      this.contextMenu.items['show_source'] = {
          'name': 'Show Source',
          callback: (key, selection, clickEvent) => {
            this.closeSplitscreen();
            this.showSource(selection);
        }
      }
    }
  }

  pasteHelperText(): string {
    if (CommonFunctions.isMac()) {
      return 'Paste (cmd + V)';
    } else {
      return 'Paste (ctrl + V)';
    }
  }

  addInsertIntoTableOptions(): void {
    if (this.model.document.pages.length > 0) {
      const menuItems = [];

      this.model.document.pages.forEach( (page, index) => {
        const menuItem = {
          key: `insert_from_page:${index}`,
          name: `Page ${index + 1} - ${this.model.document.pages[index].statementType}`,
          disabled: () => this.activePage === index, // don't allow inserting from current page
          callback: (key, selection, clickEvent) => {
            this.insertFrom(selection, index);
          }
        }
        menuItems.push(menuItem);
      });


      this.contextMenu.items['insert_from_page'] = {
        name: 'Insert From...',
        disabled: false,
        submenu: {
          // Custom option with submenu of items
          items: menuItems
        }

      }
    }

    return;
  }

  // updateCellStyling(cellChange: Array<[number, number, string, string]>): void {
  //   if (!cellChange) {
  //     return;
  //   }
  //
  //   const rowNumber = cellChange[0][0];
  //   const columnNumber = cellChange[0][1];
  //
  //   if (columnNumber > 0 && // column is a data column and not the categorization label column
  //     rowNumber <= Object.keys(MANUAL_REVIEW_HEADER_ORDER).length - 1 // row is a header row
  //   ) {
  //
  //     const scenarioTypeCell =
  //       this.handsOnTableInstance.getCell(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX, columnNumber);
  //
  //     if (scenarioTypeCell.textContent.includes(SCENARIO_TYPE_PROJECTION)) {
  //       const statementDateCell =
  //         this.handsOnTableInstance.getCell(MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX, columnNumber);
  //       Object.assign(statementDateCell.style, ProjectionRowStyle);
  //
  //       const reportingIntervalCell =
  //         this.handsOnTableInstance.getCell(MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX, columnNumber);
  //       Object.assign(reportingIntervalCell.style, ProjectionRowStyle);
  //
  //       const scenarioTypeCell =
  //         this.handsOnTableInstance.getCell(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX, columnNumber);
  //       Object.assign(scenarioTypeCell.style, ProjectionCellStyle);
  //
  //       const preparationTypeCell =
  //         this.handsOnTableInstance.getCell(MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX, columnNumber);
  //       Object.assign(preparationTypeCell.style, ProjectionRowStyle);
  //
  //       const preparedByCell =
  //         this.handsOnTableInstance.getCell(MANUAL_REVIEW_HEADER_ORDER.PREPARED_BY_INDEX, columnNumber);
  //       Object.assign(preparedByCell.style, ProjectionRowStyle);
  //     }
  //   }
  // }


  autofillMetaDataValues(cellChange: Array<[number, number, string, string]>): void {
    // format of cellChange is [row, prop, oldVal, newVal]
    if (!cellChange) {
      return;
    }
    const rowNum = cellChange[0][0];
    const colNum = cellChange[0][1];
    const oldValue = cellChange[0][2];
    const newValue = cellChange[0][3];
    const numCol = this.pageData[0].length;
    const numPage = this.model.document.pages.length;
    const numMetaFields = this.model.document.pages[this.activePage].metadata.length;

    let allBlankValues = true;

    if (oldValue == newValue) {
      return; // no material change so why autofill?
    }

    if (colNum > 0 && // column is a data column
      rowNum === MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX
    ) {
        this.setStatementOptionsforPrepTypeColumnFromScenarioType(this.activePage, colNum, newValue)
    }

    if (rowNum === MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX) {
      const inputted_statement_date = this.handsOnTableInstance.getDataAtCell(rowNum, colNum);
      if (['', null].includes(inputted_statement_date)) {
        return;
      }
      if (isValidDateCell(this.handsOnTableInstance.getDataAtCell(rowNum, colNum))
      ) {
        const now = new Date()
        this._autofillStatementDates(numCol, rowNum, allBlankValues, inputted_statement_date)

        if (new Date(inputted_statement_date) > now) {
          this.handsOnTableInstance.setDataAtCell(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX, colNum, SCENARIO_TYPE_PROJECTION)
        } else {
          this.handsOnTableInstance.setDataAtCell(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX, colNum, SCENARIO_TYPE_HISTORICAL)
        }

        // fill in missing statementDates on other pages in the corresponding columns
        for (let i = 0; i + this.activePage < numPage; i++) {
          const statementDate = this.model.document.pages[this.activePage + i].metadata[MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX][colNum];
          if (statementDate !== 'undefined' && statementDate === '' || statementDate === 'None') {
            this.model.document.pages[this.activePage + i].metadata[MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX][colNum] = inputted_statement_date;
            this.model.document.pages[this.activePage + i].metadata[MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX][colNum] = this.setScenarioTypeUsingDate(new Date(inputted_statement_date), now);
            this._validateMetadataForPage(this.activePage + i);
          }
        }
      }
      // don't want to autopopulate date so return here
      return;
    }

    // #numMetaFields - 1 is index of final meta row
    if (rowNum > (numMetaFields - 1) || colNum !== 1) {
      return; // any third+ column in prepared by row
    }

    // #numMetaFields - 1 is index of final meta row (PREPARED BY)
    if (rowNum === (numMetaFields - 1)) {
      return; // didn't implement any prepared by logic
      // that code will go here
    }

    // starts at 2 so it starts at second data column (3rd total)
    for (let columnIndex = 2; columnIndex < numCol; columnIndex++) {
      const rawVal = this.handsOnTableInstance.getDataAtCell(rowNum, columnIndex);
      let value = '';
      if (rawVal) {
        value = rawVal.toString();
      }

      if (value !== '') {
        allBlankValues = false;
      }
    }

    if (allBlankValues && rowNum !== MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX) {
      const potentialDate = this.handsOnTableInstance.getDataAtCell(rowNum - 1, colNum);

      this._setCurrentPageValuesByRow(numCol, rowNum, newValue);
      this._setAllOtherPageValuesByRow(rowNum, newValue);
      for (let pageIndex = 0; pageIndex < numPage; pageIndex++) {
        if (pageIndex !== this.activePage) {
          this._validateMetadataForPage(pageIndex);
        }
      }

      if (potentialDate && isValidDateCell(potentialDate)) {
        setTimeout(() => {

          this._autofillStatementDates(numCol, rowNum - 1, allBlankValues, potentialDate)
          const now = new Date();

          if (new Date(potentialDate) > now) {
            this.handsOnTableInstance.setDataAtCell(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX, colNum, SCENARIO_TYPE_PROJECTION)
          } else {
            this.handsOnTableInstance.setDataAtCell(MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX, colNum, SCENARIO_TYPE_HISTORICAL)
          }

          // fill in missing statementDates on other pages in the corresponding columns
          for (let i = 0; i + this.activePage < numPage; i++) {
            const statementDate = this.model.document.pages[this.activePage + i].metadata[MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX][colNum];
            if (statementDate !== 'undefined' && statementDate === '' || statementDate === 'None') {
              this.model.document.pages[this.activePage + i].metadata[MANUAL_REVIEW_HEADER_ORDER.STATEMENT_DATE_INDEX][colNum] = potentialDate;
              this.model.document.pages[this.activePage + i].metadata[MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX][colNum] = this.setScenarioTypeUsingDate(new Date(potentialDate), now);
              this._validateMetadataForPage(this.activePage + i);
            }
          }
        }, 100);
      }
    }
  }

  cleanDocumentOfNones(): void {
    this.model.document.pages = this._documentFileService.cleanDocumentOfNones(this.model.document.pages);
  }

  private _autofillStatementDates(numCol, rowNumDate, allBlankValues, newDate) : void {
    // starts at 2 so it starts at second data column (3rd total)
    for (let columnIndex = 2; columnIndex < numCol; columnIndex++) {
      const rawValDate = this.handsOnTableInstance.getDataAtCell(rowNumDate, columnIndex);
      let value_date = '';
      if (rawValDate) {
        value_date = rawValDate.toString();
      }

      if (value_date !== '') {
        allBlankValues = false;
      }

    }

    if (allBlankValues) {
      const interval_type = this.handsOnTableInstance.getDataAtCell(MANUAL_REVIEW_HEADER_ORDER.REPORTING_INTERVAL_INDEX, 2);
      if (interval_type) {
          this. _setCurrentStatementDateValuesByRow(numCol, rowNumDate, interval_type, newDate);
      }
    }
  }

  private _setCurrentStatementDateValuesByRow(numCol: number, rowNumDate: number, interval_type: string, date: string): void {
    let currentDate = date;
    for (let columnIndex = 2; columnIndex < numCol; columnIndex++) {
      currentDate = this.updateStatementDate(currentDate, interval_type)
      this.handsOnTableInstance.setDataAtCell(rowNumDate, columnIndex, currentDate);
    }
  }

  updateStatementDate(date, interval) {
    const [month, day, year] = date.split('/').map(Number);
    let updatedDate: string;

    switch (interval) {
      case 'MONTHLY':
        updatedDate = this.calculateNewDate(month, day, year, 1)
        break;
      case 'ANNUALLY':
        updatedDate = this.calculateNewDate(month, day, year + 1, 0);
        break;
      case 'QUARTERLY':
        updatedDate = this.calculateNewDate(month, day, year, 3)
        break;
      case 'SEMI_ANNUALLY':
        updatedDate = this.calculateNewDate(month, day, year, 6)
        break;
      default:
        throw new Error('Invalid interval');
    }

    return updatedDate


  }

  isLeapYear(year) { return ((year % 4 === 0 && (year % 100 !== 0)) || year % 400 === 0) }

  calculateNewDate(month: number, day: number, year: number, incr: number) {
    const true_final_day = this.isLeapYear(year) ? LEAP_YEAR_MONTH_FINAL_DAYS : MONTH_FINAL_DAYS
    const newMonth = String((month + incr) >= 13 ? (month + incr) % 12 : (month + incr)).padStart(2, '0');
    const newMonthNoPadding = String((month + incr) >= 13 ? (month + incr) % 12 : (month + incr));
    const newDay = String(true_final_day[month] == day ? true_final_day[newMonthNoPadding] : day).padStart(2, '0');
    const newYear = String((month + incr) >= 13 ? year + 1 : year);
    return `${newMonth}/${newDay}/${newYear}`;

  }

  private _setCurrentPageValuesByRow(numCol: number, numRow: number, value: string): void {
    for (let columnIndex = 2; columnIndex < numCol; columnIndex++) {
      this.handsOnTableInstance.setDataAtCell(numRow, columnIndex, value);
    }
  }

  private _setAllOtherPageValuesByRow(numRow: number, value: string): void {
    this.model.document.pages.forEach( (page, index) => {
      if (this.activePage === index) {
        return;
      }

      const rowWithLineItemNameRemoved = [...page.metadata[numRow]];
      rowWithLineItemNameRemoved.shift();

      if (rowWithLineItemNameRemoved.length === 0) { // there's no data columns
        return;
      }

      // if a value if manually deleted by an end user, the cell value is null
      const isEmpty = !rowWithLineItemNameRemoved.some(cell => !['', null].includes(cell));

      if (isEmpty) {
        for (let columnIndex = 1; columnIndex < page.metadata[numRow].length; columnIndex++) {
          page.metadata[numRow][columnIndex] = value;
          if (numRow === MANUAL_REVIEW_HEADER_ORDER.SCENARIO_TYPE_INDEX) {
            this.setStatementOptionsforPrepTypeColumnFromScenarioType(index, columnIndex, value)
          }
        }
      }
    });
  }

  private sortPageMetadata(page) {
    // Sort the page metadata by the order in MANUAL_REVIEW_HEADERS.
    page.metadata.sort((a, b) => MANUAL_REVIEW_HEADERS.indexOf(canonicalMetaLabel(a[0])) - MANUAL_REVIEW_HEADERS.indexOf(canonicalMetaLabel(b[0])));
  }

  private addMissingMetadataToPage(page) {
    // Additional column headers have been added over time and need to be added to each document that does not have
    // each of the current headers.
    let numberOfColumns = 0;
    if (page.cells.length > 0) {
      numberOfColumns = page.cells[0].length;
    } else {
      return;
    }


    const scenarioTypeInMetadata = page.metadata.filter(metadata => metadata[0] === SCENARIO_TYPE);
    if (scenarioTypeInMetadata.length === 0) {
      const metadataPeriodsToAdd = [];
      for (let i = 1; i < numberOfColumns; ++i) {
        metadataPeriodsToAdd.push('');
      }
      page.metadata.push([SCENARIO_TYPE].concat(metadataPeriodsToAdd));
    }

    const statementDateInMetadata = page.metadata.filter(metadata => metadata[0] === STATEMENT_DATE);
    if (statementDateInMetadata.length === 0) {
      page.metadata.push([STATEMENT_DATE, '', '']);
    }
  }

  /**
   * Statement options need to be created and setup for each column on each page.  This is because we need to modify
   * these options for each column individually so one statement option cannot be used for all columns; otherwise,
   * changes to the statement options would be reflected across all columns instead of the column the statement options
   * are specific to.
   */
  setupStatementOptions() {
    this.statementOptions = [];
    for (let pageNumber = 0; pageNumber < this.model.document.pages.length; ++pageNumber) {

      this.statementOptions[pageNumber] = [];
      for (
        let columnNumber = 0;
        columnNumber < this.model.document.pages[pageNumber].cells[0].length;
        ++columnNumber
      ) {
        // For each column on the page, create a statementOptions.
        this.statementOptions[pageNumber].push(
          JSON.parse(JSON.stringify(this._environmentService.getStatementOptions()))
        );
      }
      ReviewEditorComponent.setMetadataScenarioTypeUsingDate(this.model.document.pages[pageNumber], this.model);
      ReviewEditorComponent.setPrepTypeOptionsUsingScenarioType(this.model.document.pages[pageNumber], this.model, this.statementOptions[pageNumber]);
    }
  }

  /**
   * This function is used to set the allowed preparation type statement options based on the scenario type.
   * Different scenarios has different allowable lists of valid preparation types, and this needs to be reflected
   *
   * @param pageNumber page on which the statement options are being changed
   * @param colNum the column for which the options are being changed
   * @param scenarioValue the scenario value for the column
   */
  private setStatementOptionsforPrepTypeColumnFromScenarioType(pageNumber, colNum, scenarioValue) {
    if (scenarioValue === SCENARIO_TYPE_PROJECTION) {
      this.statementOptions[pageNumber][colNum][MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX]
        .values.length = 0;

      Object
        .keys(PREPARATION_TYPES_FOR_PROJECTIONS)
        .map(key =>
          this.statementOptions[pageNumber][colNum][MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX]
            .values
            .push(key)
        );
    } else if (scenarioValue === SCENARIO_TYPE_HISTORICAL) {
      this.statementOptions[pageNumber][colNum][MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX]
        .values.length = 0;

      Object
        .keys(PREPARATION_TYPES_FOR_HISTORICAL)
        .map(key =>
          this.statementOptions[pageNumber][colNum][MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX]
            .values
            .push(key)
        );
    }

    // if existing prep_type does not make valid option based on scenario type, delete the cell value
    if (!this.statementOptions[pageNumber][colNum][MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX]
      .values.includes(this.handsOnTableInstance.getDataAtCell(MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX, colNum))) {
        this.handsOnTableInstance.setDataAtCell(MANUAL_REVIEW_HEADER_ORDER.PREPARATION_TYPE_INDEX, colNum, '');
    }
  }

  private setScenarioTypeUsingDate(inputted_statement_date, current_date) {
    if (inputted_statement_date > current_date) {
      return SCENARIO_TYPE_PROJECTION;
    } else {
      return SCENARIO_TYPE_HISTORICAL;
    }
  }

  private sanitizeValueCells(page: any): void {
    if ((!page || !page.cells) || this.isArabicPage(page)) {
      return;
    }
    const fields = ['rawText', 'translatedRawText', 'text', 'value'];
    page.cells.forEach((row: any[]) => {
      row.forEach((cell: any) => {
        if (!cell || (cell.columnNumber === 0)) {
          return;
        }
        fields.forEach(field => {
          if (typeof cell[field] === 'string') {
            cell[field] = cell[field].replace(/[,$']/g, '');
            if (cell.sourceBox && cell.sourceBox.rawText !== null && cell.sourceBox.rawText !== undefined && cell.sourceBox.rawText !== '') {
              cell.sourceBox.rawText = cell[field];
            }
          }
        });
      });
    });
  }

  private syncValueToTextFields(page: any): void {
    if ((!page || !page.cells) || this.isArabicPage(page)) {
      return;
    }
    const fields = ['text', 'translatedRawText', 'rawText'];
    page.cells.forEach((row: any[]) => {
      row.forEach((cell: any) => {
        if (!cell || cell.value === null || cell.value === undefined) {
          return;
        }
        const stringValue = String(cell.value);
        fields.forEach(field => {
          if (cell[field] !== null && cell[field] !== undefined && cell[field] !== '') {
            cell[field] = stringValue;
          }
        });

        // update rawText inside sourceBox
        if (cell.sourceBox && cell.sourceBox.rawText !== null && cell.sourceBox.rawText !== undefined && cell.sourceBox.rawText !== '') {
          cell.sourceBox.rawText = stringValue;
        }
      });
    });
  }

  private _applyTranslatedLabelToGrid(rowIndex: number, nativeLabel: string, cell: ReviewEditorCell): void {
    if (this.viewInNativeLanguage) { return; }
    const translated = this.translationDict[nativeLabel];
    if (translated === undefined) { return; }
    cell.translatedRawText = translated;
    if (this.pageData?.[rowIndex] != null) {
      this.pageData[rowIndex][0] = translated;
    }
  }

  /**
   * Keeps a cell's sourceBox in sync with an edit. When the cell is cleared the value no longer maps
   * to a region in the source document, so the sourceBox is dropped — Show Source then reports "no
   * source" instead of highlighting a stale location.
   */
  private _syncSourceBoxOnChange(cell: ReviewEditorCell, newVal: string): void {
    if (!cell?.sourceBox) { return; }
    if (!newVal) {
      // Only clear the active highlight if it points at this cell's box; clearing a different cell
      // must not wipe the highlight currently shown for another cell.
      if (this.viewSourceBox === cell.sourceBox) {
        this.viewSourceBox = null;
      }
      cell.sourceBox = null;
    } else {
      cell.sourceBox.rawText = newVal;
    }
  }

  private _updateModelCellOnChange(cellChange: Array<[number, number, string, string]>): void {
    if (!cellChange) { return; }

    const changedCells = cellChange
      .filter(change => change && change[3] !== change[2])
      .map(([rowIndex, columnIndex, oldVal, newVal]) => ({ rowIndex, columnIndex, newVal: (newVal ?? '').trim() }));

    if (!changedCells.length) { return; }

    changedCells.forEach(({ rowIndex, columnIndex, newVal }) => {
      const page = this.model.document.pages[this.activePage];
      const metadataLength = page.metadata.length;
      const cellRowIndex = rowIndex - metadataLength;

      if (cellRowIndex >= 0 && !page.cells[cellRowIndex]) {
        page.cells[cellRowIndex] = this._buildNewCellRow(this.pageData[0].length);
      }

      const pageStatementOptions = this.statementOptions[this.activePage];
      if (pageStatementOptions?.length) {
        while (pageStatementOptions.length <= columnIndex) {
          pageStatementOptions.push(
            JSON.parse(JSON.stringify(this._environmentService.getStatementOptions()))
          );
        }
      }

      let celldatarow = page.cells[cellRowIndex];
      
      if (celldatarow && !celldatarow[columnIndex]) {
        for (let r = 0; r < page.cells.length; r++) {
          const row = page.cells[r];
          for (let col = 0; col <= columnIndex; col++) {
            if (!row[col]) {
              row[col] = this._buildNewCell();
            }
          }
        }
      }

      const celldata = celldatarow?.[columnIndex];

      if (!celldata) { return; }

      if (columnIndex === 0 && celldata.isMergeColumn === undefined && celldata.isNewcell === undefined) {
        celldata.text = newVal;
        celldata.rawText = newVal;
        celldata.translatedRawText = this.translationDict[newVal] ?? newVal;
        this._syncSourceBoxOnChange(celldata, newVal);
        this._applyTranslatedLabelToGrid(rowIndex, newVal, celldata);
      } else if (columnIndex === 0 && (celldata.isMergeColumn || celldata.isNewcell)) {
        celldata.rawText = newVal;
        celldata.raw_text = newVal;
        celldata.text = newVal;
        celldata.translatedRawText = this.translationDict[newVal] ?? newVal;
        this._syncSourceBoxOnChange(celldata, newVal);
        this._applyTranslatedLabelToGrid(rowIndex, newVal, celldata);
      }  else if (columnIndex !== 0 && celldata.isMergeColumn === undefined && celldata.isNewcell === undefined) {
        celldata.text = newVal;
        celldata.rawText = newVal;
        celldata.translatedRawText = this.translationDict[newVal] ?? newVal;
        this.pageData[rowIndex][columnIndex] = celldata.translatedRawText;
        this.handsOnTableInstance?.render();
        this._syncSourceBoxOnChange(celldata, newVal);
        if (celldata.value != null && celldata.value !== undefined) {
          celldata.value = newVal != null && newVal !== '' && !isNaN(Number(newVal)) 
  ? Number(newVal) 
  : null;
        }
      } else if (columnIndex !== 0 && (celldata.isMergeColumn || celldata.isNewcell)) {
        celldata.rawText = newVal;
        celldata.raw_text = newVal;
        celldata.text = newVal;
        celldata.translatedRawText = this.translationDict[newVal] ?? newVal;
        this.pageData[rowIndex][columnIndex] = celldata.translatedRawText;
        this.handsOnTableInstance?.render();
        this._syncSourceBoxOnChange(celldata, newVal);
        if (celldata.value != null) {
          celldata.value = newVal !== '' && !isNaN(Number(newVal)) ? Number(newVal) : null;
        }
      }
    });
  }
}
